import { billingEs } from "./billing.es";
import { hrEs } from "./hr.es";
import { dashboardEs } from "./dashboard.es";
import { paymentsEs } from "./payments.es";
import { reportsEs } from "./reports.es";
import type { MessageId } from "./fr";

/** Spanish catalog. Same keys and {placeholders} as fr.ts (checked by catalogs.t.ts). */
// Terms: feature = función, feature enablement = activación de función, role = rol, case (dossier) = expediente, pro = pro/profesional, client = cliente, job request (demande) = solicitud, lead (opportunité) = oportunidad, reason (motif) = motivo, reveal (afficher) = mostrar, staff member = miembro del equipo, sign out = cerrar sesión, setting (réglage) = ajuste.
export const es: Record<MessageId, string> = {
  ...reportsEs,
  ...paymentsEs,
  ...dashboardEs,
  ...billingEs,
  ...hrEs,
  // ── Brand and shell ──
  "brand.name": "Walliyo",
  "brand.initial": "W",
  "brand.console": "Consola",
  "nav.aria": "Consola",
  "nav.new": "NUEVO",
  "nav.group.configuration": "Configuración",
  "overview.eachDepartment": "Cada departamento, una línea",
  "overview.dept.marketplace": "Operaciones de mercado",
  "overview.dept.growth": "Ventas y crecimiento",
  "overview.dept.support": "Atención al cliente",
  "overview.dept.trust": "Confianza y seguridad",
  "overview.dept.finance": "Finanzas",
  "overview.dept.people": "Recursos humanos",
  "overview.dept.administration": "Administración",
  "overview.dept.product": "Ingeniería",
  "overview.clickThrough": "haga clic para ir",
  "overview.numberToWatch": "La cifra a vigilar",
  "overview.whereWalliyoWorks": "Dónde trabaja Walliyo",
  "overview.noCitiesYet": "Sin encargos este mes.",
  "overview.points": "{points} puntos",
  "overview.title.people.walliyoMonth": "Walliyo, {month}",
  "overview.people.tab.waiting": "Lo que te espera",
  "overview.people.tab.company": "La empresa",
  "overview.people.tab.weeks": "Las próximas semanas",
  "overview.people.askForTimeOff": "Pedir vacaciones",
  "overview.people.joinedThisMonth": "{count} incorporaciones este mes",
  "overview.people.joinedThisMonth.one": "1 incorporación este mes",
  "overview.people.candidates": "{count} candidatos",
  "overview.people.candidates.one": "1 candidato",
  "overview.people.medianStay": "Años, permanencia mediana",
  "overview.people.whereEverybodyIs": "Dónde está cada uno",
  "overview.people.oneLinePerTeam": "una línea por equipo",
  "overview.people.noTeamsYet": "Todavía sin equipos.",
  "overview.people.noLead": "sin responsable",
  "overview.people.nobodyCovering": "Nadie que cubra",
  "overview.people.thinWeek": "{team} pierde {away} de {size} en la semana del {week}.",
  "overview.people.whoIsWhere": "Quién está dónde",
  "overview.people.fourWeeks": "cuatro semanas, y qué cae en cada una",
  "overview.people.allTimeOff": "Todas las vacaciones",
  "overview.people.quietWeeks": "No cae nada en las próximas cuatro semanas.",
  "overview.people.mark.payrollCloses": "la nómina cierra {on}",
  "overview.people.mark.reviewsClose": "las evaluaciones cierran {on}",
  "overview.people.span.leave": "{days} días libres",
  "overview.people.span.leave.one": "1 día libre",
  "overview.people.span.leavePending": "{days} días · sin decidir",
  "overview.people.span.leavePending.one": "1 día · sin decidir",
  "overview.people.span.starts": "empieza",
  "overview.people.task.candidates": "{count} candidatos te esperan",
  "overview.people.detail.candidates": "para {second} puestos abiertos",
  "marketplace.seeAll": "Ver los {count}",
  "marketplace.findAPro": "Encontrar un pro",
  "marketplace.theMarket": "El mercado, esta semana",
  "overview.title.marketplace.theMarket": "El mercado, esta semana",
  "overview.lede.marketplace.nobodyAnswered": "{first} personas pidieron algo y nadie respondió.",
  "overview.lede.marketplace.demandNoSupply": "{first} oficios se piden donde ningún pro tiene la habilidad.",
  "overview.lede.marketplace.quiet": "Esta semana no se publicó nada.",
  "overview.lede.marketplace.allAnswered": "Todos los trabajos de esta semana recibieron presupuesto.",
  "overview.people.link.employees": "Empleados",
  "overview.people.link.recruiting": "Contratación",
  "overview.people.link.learning": "Formación",
  "overview.people.link.teams": "Equipos",
  "overview.administration.askedNotAnswered": "Pedido, sin responder",
  "overview.administration.oldestFirst": "el más antiguo primero",
  "overview.administration.nobodyWaiting": "Nadie espera una respuesta.",
  "overview.administration.row.wantsUntil": "{subject} quiere poder {asked}",
  "overview.administration.row.wantsForever": "{subject} quiere poder {asked}, sin fecha de fin",
  "overview.administration.until": "hasta el {on}",
  "overview.administration.noEndAsked": "sin fecha de fin pedida",
  "overview.administration.hoursWaiting": "{hours} horas esperando",
  "overview.administration.hoursWaiting.one": "1 hora esperando",
  "overview.administration.pastTheDeadline": "plazo superado",
  "overview.administration.nobodyTookItBack": "Nadie lo retiró",
  "overview.administration.noEndDate": "{count} accesos no tienen fecha de fin.",
  "overview.administration.noEndDate.one": "1 acceso no tiene fecha de fin.",
  "overview.administration.belongToLeavers": "{leavers} pertenecen a alguien que se fue.",
  "overview.administration.belongToLeavers.one": "1 pertenece a alguien que se fue.",
  "overview.administration.reviewThem": "Revisarlos",
  "overview.administration.criticalCapabilities": "Capacidades críticas",
  "overview.administration.ifAnyReachesOne": "Si alguna baja a una persona, nadie puede cubrir su ausencia.",
  "overview.figure.holdCritical": "Tienen algo crítico",
  "overview.lede.administration.waitingAndGrants":
    "{first} personas esperan respuesta, y {second} accesos no tienen fecha de fin.",
  "overview.lede.administration.waiting": "{first} personas esperan respuesta.",
  "overview.lede.administration.grants": "{first} accesos no tienen fecha de fin y nadie los retiró.",
  "overview.lede.administration.nothingWaiting": "Nadie espera una respuesta.",
  "overview.growth.step.signedUp": "Registrados",
  "overview.growth.step.verified": "Verificados",
  "overview.growth.step.addedSkill": "Añadieron un oficio",
  "overview.growth.step.quotedOnce": "Presupuestaron una vez",
  "overview.growth.step.wonAJob": "Ganaron un trabajo",
  "overview.growth.lost.signedUp": "nunca se registraron",
  "overview.growth.lost.verified": "se rindieron",
  "overview.growth.lost.addedSkill": "nunca lo hicieron",
  "overview.growth.lost.quotedOnce": "nunca presupuestaron",
  "overview.growth.lost.wonAJob": "nunca ganaron",
  "overview.growth.stuck": "Atascados, y vale una llamada",
  "overview.growth.stuckWho": "verificados, en un oficio con demanda, nunca presupuestaron",
  "overview.growth.nobodyStuck": "Nadie está atascado.",
  "overview.growth.row.workGoingUnanswered": "{subject}",
  "overview.growth.why.workGoingUnanswered": "verificado hace {days} días · hay trabajo en su oficio sin respuesta",
  "overview.growth.row.neverQuoted": "{subject}",
  "overview.growth.why.neverQuoted": "verificado hace {days} días · nunca ha presupuestado nada",
  "overview.growth.promoCost":
    "Ha dado {given} en descuentos frente a {earned} en comisiones de los pros que la usaron.",
  "overview.growth.promoUsed": "{used} pros la han usado",
  "overview.growth.beforeYouCampaign": "Antes de hacer campaña",
  "overview.growth.notificationsAreFree":
    "Las notificaciones en la aplicación llegan a estos pros sin coste. El SMS cuesta por mensaje, y aquí los datos se pagan por hora.",
  "overview.action.call": "Llamar",
  "overview.lede.growth.neverQuoted":
    "{first} pros se registraron y nunca presupuestaron nada. Ese es todo el trabajo.",
  "overview.lede.growth.nobodySignedUp": "Nadie se registró este mes.",
  "overview.lede.growth.allWorking": "Todos los pros registrados han presupuestado algo.",
  "overview.support.dueInMinutes.one": "plazo en 1 minuto",
  "overview.support.dueInHours.one": "plazo en 1 hora",
  "overview.support.overdue.one": "1 minuto pasado el plazo",
  "overview.support.nobodyOwnsWhy.one":
    "Un caso sin responsable es un caso que nadie responde. El más antiguo lleva 1 día.",
  "overview.people.days.one": "1 día",
  "overview.people.payrollOpen.one": "{month} está abierto. Cierra mañana.",
  "overview.people.detail.leave.one": "Pedido hace 1 día · eres su responsable",
  "overview.finance.waiting.one": "1 día esperando",
  "overview.trust.row.appealWaiting.one": "Apelación esperando 1 día",
  "overview.trust.legalHoldsWhy.one":
    "1 cuenta está retenida. Nada sobre ella puede borrarse, ni por una solicitud de datos.",
  "overview.product.unconfirmed.one": "1 retorno sin confirmar, hace {minutes} minutos.",
  "overview.product.whatIsBroken": "Qué está roto",
  "overview.product.worstFirst": "lo peor primero",
  "overview.product.nothingBroken": "Nada está roto.",
  "overview.product.unconfirmed": "{count} retornos sin confirmar, el más antiguo hace {minutes} minutos.",
  "overview.product.unconfirmedWhy": "Cada uno es un pro que pagó y no está marcado como pagado.",
  "overview.product.replayThem": "Reproducirlos",
  "overview.product.rolledOut": "Desplegado a clientes",
  "overview.product.tally.everybody": "Todos",
  "overview.product.tally.someAccounts": "Algunas cuentas",
  "overview.product.tally.off": "Desactivado",
  "overview.figure.rolloutsInFlight": "Despliegues en curso",
  "overview.product.row.incidentOpen": "{subject}",
  "overview.product.why.incidentOpen": "abierto desde hace {amount} · los clientes ven el aviso",
  "overview.product.row.ruleScheduled": "La regla entra en vigor {at}",
  "overview.product.why.ruleScheduled": "{subject} {amount} · nadie ha dado el visto bueno",
  "overview.product.row.experimentRunning": "{subject} en el {days}% de los clientes",
  "overview.product.why.experimentRunning": "en curso desde {on} · listo para decidir",
  "overview.figure.incidentsOpen": "Incidentes abiertos",
  "overview.lede.product.incidentAndCallbacks": "{first} incidente está abierto, y {second} retornos de pago fallaron.",
  "overview.lede.product.incident": "{first} incidente está abierto.",
  "overview.lede.product.callbacks": "{first} retornos de pago fallaron y nadie los ha reproducido.",
  "overview.lede.product.rulesScheduled": "{first} reglas entran en vigor sin nadie presente.",
  "overview.lede.product.nothingBroken": "Nada está roto.",
  "overview.finance.aboveLimit": "Por encima del límite de alguien",
  "overview.finance.theyAskedYouDecide": "lo pidieron, tú decides",
  "overview.finance.nothingWaitingHere": "Nada espera una aprobación.",
  "overview.finance.theirLimit": "límite {ceiling}",
  "overview.finance.noLimit": "no tiene límite",
  "overview.finance.waiting": "{days} días esperando",
  "overview.finance.today": "hoy",
  "overview.finance.subscriptions": "Suscripciones",
  "overview.finance.tally.endingSoon": "Renovación en 7 días",
  "overview.finance.tally.suspended": "Pago fallido",
  "overview.finance.tally.ended": "Terminadas, no renovadas",
  "overview.finance.promosRunning": "Promociones en curso",
  "overview.finance.promoEnds": "{name} termina el {on}.",
  "overview.finance.promoNoEnd": "{name} no tiene fecha de fin.",
  "overview.finance.promoCost": "Ha dado {given} en descuentos frente a {earned} en comisiones.",
  "overview.figure.feesCollected": "Comisiones cobradas",
  "overview.figure.jobsBilled": "Trabajos facturados",
  "overview.figure.renewalsFailed": "Renovaciones fallidas",
  "overview.lede.finance.waitingOnApproval": "{first} correcciones esperan a alguien que pueda aprobarlas.",
  "overview.lede.finance.renewalsFailed": "{first} renovaciones fallaron y nadie las ha retomado.",
  "overview.lede.finance.renewingSoon": "{first} suscripciones se renuevan esta semana.",
  "overview.lede.finance.nothingWaiting": "Nada espera una aprobación.",
  "overview.trust.needsDecision": "A decidir hoy",
  "overview.trust.mostSeriousFirst": "lo más grave primero",
  "overview.trust.nothingUrgentHere": "Nada que decidir hoy.",
  "overview.trust.verificationQueue": "Cola de verificación",
  "overview.trust.whyVerify": "Un pro que espera verificación no puede ser encontrado ni contratado.",
  "overview.trust.tally.underADay": "Esperando menos de 24 h",
  "overview.trust.tally.overADay": "Más de 24 h",
  "overview.trust.legalHolds": "Retenciones legales",
  "overview.trust.legalHoldsWhy":
    "{count} cuentas están retenidas. Nada sobre ellas puede borrarse, ni por una solicitud de datos.",
  "overview.trust.row.criticalUnreferred": "{subject}",
  "overview.trust.why.criticalUnreferred": "crítico · no remitido a la policía",
  "overview.trust.row.restrictionExpiring": "La sanción se levanta sola el {on}",
  "overview.trust.why.restrictionExpiring": "{subject} · levántala, o se levanta sola",
  "overview.trust.row.appealWaiting": "Apelación esperando {days} días",
  "overview.trust.why.appealWaiting": "una cuenta sancionada pidió que se reconsiderara",
  "overview.figure.underLegalHold": "Bajo retención legal",
  "overview.lede.trust.criticalUnreferred": "{first} denuncias son críticas y ninguna se ha remitido.",
  "overview.lede.trust.waitingOverADay": "{first} pros esperan más de un día la verificación.",
  "overview.lede.trust.waitingToVerify": "{first} pros esperan la verificación.",
  "overview.lede.trust.nothingUrgent": "Nada que decidir hoy.",
  "overview.support.runningOut": "Se acaba el tiempo",
  "overview.support.soonestFirst": "plazo más próximo primero",
  "overview.support.nothingDue": "Ningún plazo está cerca.",
  "overview.support.whereFrom": "De dónde llegaron",
  "overview.support.noChannels": "Ningún caso abierto.",
  "overview.support.channel.chat": "En la aplicación",
  "overview.support.channel.phone": "Teléfono",
  "overview.support.channel.email": "Correo electrónico",
  "overview.support.nobodyOwns": "{count} sin responsable",
  "overview.support.nobodyOwnsWhy":
    "Un caso sin responsable es un caso que nadie responde. El más antiguo lleva {days} días.",
  "overview.support.takeOldest": "Tomar el más antiguo",
  "overview.support.row.mine": "tuyo",
  "overview.support.row.theirs": "de otra persona",
  "overview.support.row.unclaimed": "sin responsable",
  "overview.support.dueInMinutes": "plazo en {minutes} minutos",
  "overview.support.dueInHours": "plazo en {hours} horas",
  "overview.support.overdue": "{minutes} minutos pasado el plazo",
  "overview.figure.toFirstReply": "Hasta la primera respuesta",
  "overview.action.reply": "Responder",
  "overview.action.claim": "Tomar",
  "overview.lede.support.breaching": "{first} casos van a pasarse de plazo.",
  "overview.lede.support.breachingSomeYours": "{first} casos van a pasarse de plazo. {second} son tuyos.",
  "overview.lede.support.unclaimed": "{first} casos esperan que alguien los tome.",
  "overview.lede.support.nothingBreaching": "Ningún caso va a pasarse de plazo.",
  "overview.against": "frente a {month}",
  "overview.title.executive.walliyoMonth": "Walliyo, {month}",
  "overview.title.people.thingsNeedYou": "{count} cosas te necesitan",
  "overview.title.people.nothingNeedsYou": "Nada te necesita",
  "overview.title.support.bonjour": "Hola, {name}",
  "overview.title.trust.safetyToday": "Seguridad, hoy",
  "overview.title.finance.theMoney": "El dinero, esta semana",
  "overview.title.product.themOrUs": "¿Son ellos o somos nosotros?",
  "overview.title.growth.gettingProsWorking": "Poner a trabajar a los pros",
  "overview.title.administration.whoMayDoWhat": "Quién puede hacer qué",
  "overview.title.marketplace.plain": "Vista general",
  "overview.title.legal.plain": "Vista general",
  "overview.lede.executive.grewFaster": "El mercado creció. Lo que no pudo atender creció más rápido.",
  "overview.lede.executive.wentUnanswered": "{first} solicitudes quedaron sin respuesta este mes.",
  "overview.lede.executive.quiet": "Este mes no se publicó nada.",
  "overview.lede.executive.holding": "{second} solicitudes publicadas, y todas encontraron respuesta.",
  "overview.people.allClear": "Nada te necesita. Todo está atendido.",
  "overview.people.theCompany": "La empresa",
  "overview.people.employees": "empleados",
  "overview.people.teams": "equipos",
  "overview.people.yours": "Lo tuyo",
  "overview.people.leaveLeft": "Vacaciones restantes",
  "overview.people.days": "{days} días",
  "overview.people.noAllowance": "sin definir",
  "overview.people.coursesDue": "Cursos pendientes",
  "overview.people.payroll": "Nómina",
  "overview.people.payrollOpen": "{month} está abierto. Cierra en {days} días.",
  "overview.people.payrollDue": "{month} está abierto y su fecha de cierre ya pasó.",
  "overview.people.payrollClosed": "{month} está cerrado.",
  "overview.people.linesChecked": "{checked} de {total} líneas revisadas",
  "overview.people.task.leave": "{subject} pide {count} días libres",
  "overview.people.detail.leave": "Pedido hace {days} días · eres su responsable",
  "overview.people.task.grantsNoEndDate": "{count} accesos no tienen fecha de fin",
  "overview.people.detail.grantsNoEndDate": "{second} pertenecen a personas que diriges",
  "overview.people.task.reviews": "{count} evaluaciones te toca escribir",
  "overview.people.detail.reviews": "cierra el {on}",
  "overview.people.task.starting": "{subject} empieza el {on}",
  "overview.people.detail.starting": "rol ya asignado",
  "overview.people.task.startingNoRole": "{subject} empieza el {on}",
  "overview.people.detail.startingNoRole": "sin rol asignado",
  "overview.action.decide": "Decidir",
  "overview.action.review": "Revisar",
  "overview.action.write": "Escribir",
  "overview.action.setUp": "Configurar",
  "overview.action.open": "Abrir",
  "overview.hero.jobsPosted": "Encargos publicados",
  "overview.hero.gotAQuote": "Con presupuesto",
  "overview.hero.fees": "Comisiones, GNF",
  "overview.hero.activePros": "Profesionales activos",
  "overview.hero.employees": "Empleados",
  "overview.headline.unanswered": "{count} encargos sin respuesta · {second} oficios sin cobertura",
  "overview.headline.neverQuoted": "{count} profesionales inscritos y nunca respondieron",
  "overview.headline.breaching": "{count} casos a punto de vencer · {second} sin responsable",
  "overview.headline.criticalUnreferred": "{count} denuncias críticas sin derivar · {second} verificaciones pendientes",
  "overview.headline.approvalsWaiting": "{count} correcciones esperando · {second} vencidas",
  "overview.headline.approvalsWaiting.zero": "{count} correcciones esperando · nada vencido",
  "overview.headline.grantsWithoutExpiry": "{count} accesos sin fecha de fin · {second} solicitudes de acceso",
  "overview.headline.incidentsOpen": "{count} incidente abierto · {second} retornos fallidos",
  "overview.headline.startingAndPayroll": "{count} incorporaciones · nómina abierta",
  "overview.watch.demandOutrunningSupply":
    "Los trabajos suben {first}% y la parte que recibe presupuesto baja {second} puntos. La demanda llega más rápido de lo que la oferta puede responder.",
  "overview.watch.nobodyAnswered": "{first} trabajos se publicaron sin que nadie presupuestara.",
  "roles.new.department": "Departamento",
  "roles.new.pickDepartment": "Elegir un departamento…",
  "roles.new.departmentNeeded":
    "Elija el departamento del perfil: un tester QA es Producto e ingeniería, un contable es Finanzas.",
  "screen.experiments": "Experimentos",
  "screen.leads": "Contactos",
  "screen.campaigns": "Campañas",
  "screen.referrals": "Referidos",
  "experiments.title": "Experimentos",
  "experiments.sub": "Activar una funcionalidad para una parte de los clientes y comparar.",
  "experiments.loadFailed": "No se han podido cargar los experimentos.",
  "experiments.none": "Ningún experimento.",
  "experiments.start": "Iniciar un experimento",
  "experiments.created": "{reference} creado",
  "experiments.moved": "{reference} al {percent}%",
  "experiments.ship": "Generalizar",
  "experiments.stop": "Detener",
  "experiments.sticky": "Una persona se queda en su grupo: el grupo se calcula, nunca se sortea.",
  "experiments.col.name": "Experimento",
  "experiments.col.feature": "Funcionalidad",
  "experiments.col.share": "Parte",
  "experiments.col.status": "Estado",
  "experiments.col.ramp": "Ampliar",
  "experiments.status.draft": "Borrador",
  "experiments.status.running": "En curso",
  "experiments.status.stopped": "Detenido",
  "experiments.status.shipped": "Generalizado",
  "experiments.startTitle": "Iniciar un experimento",
  "experiments.startBody": "Empieza al 0%: crearlo y abrirlo son dos decisiones.",
  "experiments.startSubmit": "Crear",
  "experiments.featureNeeded": "Elija una funcionalidad de cliente.",
  "experiments.pickFeature": "Elegir…",
  "experiments.field.name": "Qué se prueba",
  "experiments.field.feature": "Funcionalidad de cliente",
  "experiments.field.featureHint": "Un experimento corre sobre clientes, nunca sobre el personal.",
  "experiments.field.metric": "Qué se mide",
  "experiments.field.metricHint": "Lo lee una persona, no el código.",
  "leads.title": "Contactos",
  "leads.sub": "Profesionales que queremos y aún no tenemos.",
  "leads.loadFailed": "No se han podido cargar los contactos.",
  "leads.none": "Ningún contacto.",
  "leads.add": "Añadir un contacto",
  "leads.added": "{name} añadido",
  "leads.moved": "{name} actualizado",
  "leads.markContacted": "Contactado",
  "leads.markLost": "Perdido",
  "leads.nameNeeded": "Dé el nombre del profesional.",
  "leads.status.new": "Nuevo",
  "leads.status.contacted": "Contactado",
  "leads.status.signed_up": "Inscrito",
  "leads.status.lost": "Perdido",
  "leads.source.coverage_gap": "Falta de cobertura",
  "leads.source.referral": "Recomendación",
  "leads.source.inbound": "Entrante",
  "leads.source.other": "Otro",
  "leads.addTitle": "Añadir un contacto",
  "leads.addBody": "Alguien que aún no está en la plataforma.",
  "leads.addSubmit": "Añadir",
  "leads.field.name": "Nombre",
  "leads.field.phone": "Teléfono",
  "leads.field.trade": "Oficio",
  "leads.field.city": "Ciudad",
  "leads.field.source": "Origen",
  "campaigns.title": "Campañas",
  "campaigns.sub": "Mensajes a profesionales y clientes.",
  "campaigns.loadFailed": "No se han podido cargar las campañas.",
  "campaigns.none": "Ninguna campaña.",
  "campaigns.draft": "Preparar una campaña",
  "campaigns.drafted": "{reference} lista: {count} destinatarios",
  "campaigns.send": "Enviar",
  "campaigns.sent": "Enviada a {count} personas",
  "campaigns.wentTo": "Fue a {count}",
  "campaigns.budgetNeeded": "Una campaña SMS necesita presupuesto.",
  "campaigns.onlyInactive": "Solo quienes no han hecho nada",
  "campaigns.inAppFree": "Gratis: la notificación llega en la aplicación.",
  "campaigns.smsCosts": "De pago: cada SMS cuesta, y aquí los datos se pagan por hora.",
  "campaigns.smsLength": "{count} caracteres (más de 160 son dos SMS por persona)",
  "campaigns.channel.in_app": "En la aplicación",
  "campaigns.channel.sms": "SMS",
  "campaigns.audience.pros": "Profesionales",
  "campaigns.audience.customers": "Clientes",
  "campaigns.draftTitle": "Preparar una campaña",
  "campaigns.draftBody": "El número de destinatarios se vuelve a contar al enviar.",
  "campaigns.draftSubmit": "Preparar",
  "campaigns.field.name": "Nombre",
  "campaigns.field.channel": "Canal",
  "campaigns.field.audience": "Destinatarios",
  "campaigns.field.body": "Mensaje",
  "campaigns.field.bodyHint": "En francés, visible para todos.",
  "campaigns.field.budget": "Presupuesto (GNF)",
  "campaigns.field.budgetHint": "Lo que se autoriza gastar.",
  "referrals.title": "Referidos",
  "referrals.sub": "Profesionales traídos por otros profesionales.",
  "referrals.loadFailed": "No se han podido cargar los referidos.",
  "referrals.none": "Ningún referido.",
  "referrals.unused": "Código sin usar",
  "referrals.paidOut": "{amount} {currency} pagados",
  "referrals.paidWhen": "Se paga cuando el profesional referido gana su primer trabajo.",
  "referrals.status.open": "Abierto",
  "referrals.status.signed_up": "Inscrito",
  "referrals.status.rewarded": "Pagado",
  "referrals.status.expired": "Caducado",
  "screen.incidents": "Incidencias",
  "incidents.title": "Incidencias",
  "incidents.sub": "Qué está roto, y qué se les dice a las personas.",
  "incidents.loadFailed": "No se han podido cargar las incidencias.",
  "incidents.openOne": "Abrir una incidencia",
  "incidents.happeningNow": "En curso",
  "incidents.allWell": "Todo funciona.",
  "incidents.over": "Terminadas",
  "incidents.open": "Abrir",
  "incidents.notAnnounced": "Sin anunciar",
  "incidents.started": "Empezó el {when}",
  "incidents.opened": "{reference} abierta",
  "incidents.updated": "{reference} actualizada",
  "incidents.status.open": "Abierta",
  "incidents.status.monitoring": "En observación",
  "incidents.status.resolved": "Resuelta",
  "incidents.severity.degraded": "Degradado",
  "incidents.severity.down": "Fuera de servicio",
  "incidents.openTitle": "Abrir una incidencia",
  "incidents.openBody":
    "Mientras esté abierta, cada cliente ve un aviso. Déjelo vacío para seguir la incidencia sin anunciar nada.",
  "incidents.openSubmit": "Abrir",
  "incidents.titleNeeded": "Diga qué está roto, en pocas palabras.",
  "incidents.causeNeeded": "Diga por qué ocurrió antes de cerrar.",
  "incidents.updateSubmit": "Guardar",
  "incidents.field.title": "Qué está roto",
  "incidents.field.severity": "Gravedad",
  "incidents.field.banner": "Mensaje a los clientes",
  "incidents.field.bannerHint": "En francés, visible para todos. Vacío: no se avisa a nadie.",
  "incidents.field.status": "Estado",
  "incidents.field.note": "Nota",
  "incidents.field.noteHint": "Va al historial, que nada puede reescribir.",
  "incidents.field.cause": "Causa",
  "screen.overview": "Resumen",
  "overview.sub": "Lo que hoy le espera.",
  "overview.loadFailed": "No se ha podido cargar el resumen.",
  "overview.needsYou": "Lo que le necesita",
  "overview.nothingToShow": "Nada que mostrar: no tiene nada de lo que este departamento consulta.",
  "overview.waitingDays": "{days} d esperando",
  "overview.figure.leaveToDecide": "Vacaciones por decidir",
  "overview.figure.reviewsOwed": "Evaluaciones por escribir",
  "overview.figure.openRoles": "Puestos abiertos",
  "overview.figure.startingSoon": "Incorporaciones esta semana",
  "overview.figure.payrollOpen": "Nóminas abiertas",
  "overview.figure.leaversStillHolding": "Salidos que aún tienen acceso",
  "overview.figure.accessRequests": "Solicitudes de acceso",
  "overview.figure.grantsWithoutExpiry": "Accesos sin fecha de fin",
  "overview.figure.rolesHeldByNobody": "Perfiles que nadie tiene",
  "overview.figure.staffWithSignIn": "Empleados activos",
  "overview.figure.casesBreaching": "Casos a punto de vencer",
  "overview.figure.casesMine": "Sus casos",
  "overview.figure.casesUnclaimed": "Casos sin responsable",
  "overview.figure.casesOpen": "Casos abiertos",
  "overview.figure.criticalUnreferred": "Denuncias críticas sin derivar",
  "overview.figure.verificationsWaiting": "Verificaciones pendientes",
  "overview.figure.verificationsOverADay": "Esperando más de 24 h",
  "overview.figure.reportsOpen": "Denuncias abiertas",
  "overview.figure.restrictionsToReview": "Restricciones por revisar",
  "overview.figure.legalHolds": "Retenciones legales",
  "overview.figure.approvalsWaiting": "Correcciones por aprobar",
  "overview.figure.feesThisMonth": "Pagos este mes",
  "overview.figure.creditsOutstanding": "Créditos pendientes",
  "overview.figure.subscriptionsEndingSoon": "Suscripciones que acaban en 7 d",
  "overview.figure.providerEventsFailed": "Retornos del proveedor fallidos",
  "overview.figure.rulesScheduled": "Reglas programadas",
  "overview.figure.featureExceptions": "Excepciones de funcionalidad",
  "overview.figure.prosSignedUp": "Profesionales inscritos (30 d)",
  "overview.figure.prosApproved": "Profesionales aprobados (30 d)",
  "overview.figure.prosNeverQuoted": "Profesionales que nunca respondieron",
  "overview.figure.prosWithWork": "Profesionales con trabajo",
  "overview.figure.promosRunning": "Promociones en curso",
  "overview.figure.jobsThisMonth": "Encargos este mes",
  "overview.figure.customers": "Cuentas de clientes",
  "overview.item.caseDueOwned": "Su caso está por vencer",
  "overview.item.caseDueUnclaimed": "Caso sin responsable por vencer",
  "screen.marketplaceOverview": "Resumen",
  "screen.jobs": "Encargos",
  "screen.providers": "Profesionales",
  "screen.categories": "Oficios",
  "screen.marketplaceReviews": "Reseñas",
  "marketplace.title": "El mercado",
  "marketplace.sub": "Lo que se pidió, quién sabe hacerlo, y dónde nadie sabe.",
  "marketplace.loadFailed": "No se ha podido cargar el mercado.",
  "marketplace.posted": "Encargos publicados",
  "marketplace.gotQuote": "Con presupuesto",
  "marketplace.accepted": "Aceptados",
  "marketplace.noBids": "Nadie respondió",
  "marketplace.toFirstQuote": "Hasta el 1.er presupuesto",
  "marketplace.hoursShort": "{hours} h",
  "marketplace.nobodyBid": "Nadie respondió a estos",
  "marketplace.nobodyBidHint": "Los más antiguos primero.",
  "marketplace.allAnswered": "Todo lo publicado ha tenido respuesta.",
  "marketplace.waitingDays": "{days} d esperando",
  "marketplace.noProHere": "aquí nadie hace esto",
  "marketplace.prosNearby": "{count} profesionales cerca",
  "marketplace.demandNoSupply": "Demanda sin oferta",
  "marketplace.demandNoSupplyHint": "Pedido en los últimos 30 días, sin profesional en la zona.",
  "marketplace.everywhereCovered": "Cada ciudad tiene profesionales para lo que pide.",
  "marketplace.activePros": "{count} profesionales pueden trabajar hoy",
  "marketplace.jobs": "Encargos",
  "marketplace.jobsSub": "Cada encargo, y hasta dónde llegó.",
  "marketplace.noJobs": "Ningún encargo coincide.",
  "marketplace.filter.all": "Todos",
  "marketplace.filter.no_quotes": "Sin presupuesto",
  "marketplace.filter.requested": "Publicados",
  "marketplace.filter.quoted": "Con presupuesto",
  "marketplace.filter.accepted": "Aceptados",
  "marketplace.filter.cancelled": "Cancelados",
  "marketplace.status.requested": "Publicado",
  "marketplace.status.quoted": "Con presupuesto",
  "marketplace.status.accepted": "Aceptado",
  "marketplace.status.cancelled": "Cancelado",
  "marketplace.status.declined": "Rechazado",
  "marketplace.status.expired": "Caducado",
  "marketplace.col.job": "Encargo",
  "marketplace.col.city": "Ciudad",
  "marketplace.col.posted": "Publicado",
  "marketplace.col.quotes": "Presupuestos",
  "marketplace.col.estimate": "Estimación",
  "marketplace.col.status": "Estado",
  "marketplace.providers": "Profesionales",
  "marketplace.providersSub": "Quién puede trabajar, y quién aún no trabaja.",
  "marketplace.noPros": "Ningún profesional coincide.",
  "marketplace.neverQuoted": "Nunca respondieron",
  "marketplace.allPros": "Todos",
  "marketplace.unverified": "Sin verificar",
  "marketplace.col.pro": "Profesional",
  "marketplace.col.skills": "Oficios",
  "marketplace.col.quotesGiven": "Presupuestos dados",
  "marketplace.col.rating": "Valoración",
  "marketplace.categories": "Oficios",
  "marketplace.categoriesSub": "Lo que la gente pide, y si alguien sabe hacerlo.",
  "marketplace.noCategories": "Ningún oficio que mostrar.",
  "marketplace.noDemand": "Sin demanda",
  "marketplace.coverageHint": "La cobertura es la parte de encargos con al menos un presupuesto.",
  "marketplace.col.category": "Oficio",
  "marketplace.col.jobs": "Encargos",
  "marketplace.col.pros": "Profesionales",
  "marketplace.col.coverage": "Cobertura",
  "marketplace.col.uncovered": "Ciudades sin profesional",
  "marketplace.reviews": "Reseñas",
  "marketplace.reviewsSub": "Lo que los clientes dicen de los profesionales.",
  "marketplace.noReviews": "Ninguna reseña coincide.",
  "marketplace.poorOnly": "3 estrellas o menos",
  "marketplace.allReviews": "Todas",
  "marketplace.averageIs": "Media {average} sobre {total} reseñas",
  "nav.group.everyone": "Todo el mundo",
  "workspace.yours": "Sus espacios",
  "department.executive": "Dirección",
  "department.executive.blurb": "La empresa de un vistazo",
  "department.marketplace": "Operaciones del mercado",
  "department.marketplace.blurb": "Encargos, profesionales, categorías",
  "department.support": "Atención al cliente",
  "department.support.blurb": "Casos, clientes y profesionales",
  "department.trust": "Confianza y seguridad",
  "department.trust.blurb": "Verificaciones, denuncias, restricciones",
  "department.finance": "Finanzas",
  "department.finance.blurb": "Comisiones, correcciones, suscripciones",
  "department.growth": "Ventas y crecimiento",
  "department.growth.blurb": "Captar profesionales, promociones",
  "department.product": "Producto e ingeniería",
  "department.product.blurb": "Incidencias, reglas, despliegues",
  "department.people": "Recursos humanos",
  "department.people.blurb": "Contratación, empleo, nóminas",
  "department.administration": "Administración y seguridad",
  "department.administration.blurb": "Perfiles, permisos, registro",
  "department.legal": "Legal y cumplimiento",
  "department.legal.blurb": "Términos, datos, solicitudes legales",
  "nav.signedInAs": "Sesión iniciada como",
  "nav.screenCount": "{roles} · {visible} pantallas de {total}",
  "profile.title": "Tu perfil",
  "profile.open": "Abrir tu perfil",
  "profile.identity": "Tú",
  "profile.email": "Correo",
  "profile.roles": "Roles",
  "profile.permits": "Lo que tus roles permiten",
  "profile.screensOpen": "{visible} de {total} pantallas abiertas",
  "profile.actionCount": "{count} acciones disponibles",
  "profile.noPermissions": "Sin permisos con límite: nada necesita aprobación.",
  "profile.preferences": "Preferencias",
  "profile.emailLabel": "Correo:",
  "profile.uuidLabel": "UUID:",
  "profile.rolesLabel": "Roles",
  "profile.figure.screens": "pantallas abiertas para ti",
  "profile.figure.actions": "acciones que puedes hacer",
  "profile.figure.ceilings": "con un límite",
  "profile.ceilingHeading": "Lo máximo que puedes hacer solo",
  "profile.ceilingHint":
    "Por encima de un límite la acción se abre igualmente: tu intento se convierte en una solicitud de aprobación.",
  "profile.signOutHint": "Tendrás que iniciar sesión otra vez.",
  "header.signOut": "Cerrar sesión",
  "header.signingOut": "Cerrando sesión…",
  "header.language": "Idioma de la consola",
  "header.theme": "Tema",
  "header.theme.system": "Sistema",
  "header.theme.light": "Claro",
  "header.theme.dark": "Oscuro",
  "header.languageNotSaved": "Solo para esta sesión: esta elección aún no se guarda en su cuenta.",
  "shell.loading": "Cargando la consola…",
  "shell.loadFailed": "No se pudo cargar su perfil",
  "shell.retry": "Reintentar",
  "shell.refreshFailed": "No se pudieron actualizar sus permisos. La consola muestra los cargados a las {time}.",
  "shell.noScreens": "No hay ninguna pantalla abierta para sus roles ({roles}). Pida acceso a un responsable.",
  "shell.noRoles": "Todavía no tiene ninguna pantalla accesible. Pida un rol a un responsable.",
  "connection.retrying": "Conexión inestable · nuevo intento {attempt} de {max}…",

  // ── Screens ──
  "screen.dashboard": "Panel",
  "screen.myRequests": "Mis solicitudes",
  "screen.myTimeOff": "Mis vacaciones",
  "myTimeOff.sub": "Tu saldo, lo que has pedido, y quién más está fuera.",
  "myRequests.sub": "Lo que has pedido poder hacer, y en qué punto está cada respuesta.",
  "screen.as.directory": "Directorio",
  "screen.as.market": "Mercado",
  "screen.as.employees": "Empleados",
  "screen.as.recruiting": "Contratación",
  "screen.as.learning": "Formación",
  "screen.as.payroll": "Nómina",
  "screen.as.money": "Dinero",
  "screen.as.workforce": "Plantilla",
  "screen.as.accounts": "Cuentas",
  "screen.cases": "Expedientes",
  "screen.customers": "Clientes y pros",
  "screen.verifications": "Verificaciones",
  "screen.reports": "Denuncias",
  "screen.billing": "Precios y suscripciones",
  "screen.payments": "Pagos",
  "screen.features": "Activaciones de funcionalidades",
  "screen.userSettings": "Ajustes de usuario",
  "settings.title": "Ajustes de usuario",
  "settings.tab.app": "Usuario de la app",
  "settings.tab.employee": "Empleado",
  "settings.col.setting": "Ajustes",
  "settings.default": "Valor predeterminado",
  "settings.setHere": "Fijado para esta persona",
  "settings.clear": "Borrar",
  "settings.none": "Ningún ajuste coincide.",
  "screen.actions": "Acciones",
  "actions.title": "Acciones",
  "actions.col.action": "Acción",
  "actions.effect.read": "Lectura",
  "actions.effect.write": "Escritura",
  "actions.alsoGates": "+{count} más",
  "actions.col.area": "Área",
  "actions.col.effect": "Efecto",
  "actions.col.audited": "Auditada",
  "actions.col.needs": "Exige",
  "actions.needs.nothing": "Nada",
  "actions.yes": "Sí",
  "actions.no": "No",
  "actions.surface.system": "Sistema",
  "actions.search": "Buscar una acción",
  "actions.none": "Ninguna acción coincide.",
  "screen.permissions": "Permisos",
  "permissions.title": "Permisos",
  "permissions.tab.role": "Rol",
  "permissions.tab.employee": "Empleado",
  "permissions.col.limit": "Límite",
  "permissions.col.expires": "Caduca",
  "permissions.source.staffRole": "Por rol",
  "permissions.source.staffMember": "Excepción",
  "permissions.source.allStaff": "Todo el personal",
  "permissions.source.default": "Ninguno",
  "permissions.noLimit": "Sin tope",
  "permissions.featureOff": "La función está apagada",
  "permissions.approvable": "Puede pedir",
  "permissions.pickRole": "Elija un rol",
  "audit.kind.featureEnablements": "Activaciones de funcionalidades",
  "audit.kind.permissions": "Permisos",
  "audit.kind.teams": "Roles y equipos",
  "audit.kind.actions": "Acciones",
  "audit.kind.userSettings": "Ajustes de usuario",
  "screen.roles": "Roles",
  "roles.title": "Roles",
  "roles.grants.none": "Aún no concede nada",
  "roles.grants.permissions": "Cambiar sus permisos",
  "roles.grants.features": "Cambiar sus funcionalidades",
  "roles.explain":
    "Lo que alguien sabe y está autorizado a hacer. Un rol es el mismo en todos los equipos: dirigir no lo es, eso se define en Equipos.",
  "roles.col.name": "Rol",
  "roles.col.description": "Qué abarca",
  "roles.col.members": "Personas",
  "roles.none": "Aún no hay roles.",
  "roles.loading": "Cargando los roles…",
  "roles.loadFailed": "No se han podido leer los roles.",
  "roles.retry": "Reintentar",
  "roles.edit": "Renombrar",
  "roles.save": "Guardar",
  "roles.cancel": "Cancelar",
  "roles.created": "Rol creado.",
  "roles.new": "Nuevo rol",
  "roles.new.title": "Nuevo rol",
  "roles.back": "← Todos los roles",
  "roles.opens": "Qué abre",
  "roles.opens.explain":
    "Las pantallas que este rol alcanza, según las casillas de la izquierda. No es una lista aparte: una pantalla aparece porque el rol tiene lo necesario, y la API comprueba lo mismo.",
  "roles.new.name": "Nombre",
  "roles.new.description": "Qué abarca",
  "roles.new.create": "Crear el rol",
  "roles.new.nameNeeded": "Ponle un nombre al rol.",
  "roles.new.grantsNeeded": "Elige para qué sirve el rol: un rol que no concede nada no tiene efecto.",
  "roles.new.grants": "Qué puede usar",
  "roles.new.permissions": "Qué puede hacer",
  "roles.lock.critical": "Solo un Owner puede concederlo.",
  "roles.lock.high": "Solo puedes conceder lo que tú mismo tienes.",
  "roles.lock.readOnly": "No puedes conceder en un rol.",
  "roles.lock.featureFirst": "Activa primero aquello sobre lo que se apoya.",
  "screen.teams": "Equipos",
  "teams.title": "Equipos",
  "teams.col.name": "Equipo",
  "teams.col.purpose": "Para qué sirve",
  "teams.col.lead": "Dirigido por",
  "teams.col.members": "Personas",
  "teams.col.status": "Estado",
  "teams.col.person": "Persona",
  "teams.col.roles": "Rol",
  "teams.col.onTeam": "En el equipo",
  "teams.col.leads": "Lo dirige",
  "teams.open": "Abrir",
  "teams.back": "Todos los equipos",
  "teams.none": "Aún no hay equipos.",
  "teams.noMembers": "Todavía no hay nadie en este equipo.",
  "teams.noOthers": "Todo el mundo ya está en este equipo.",
  "teams.loading": "Cargando los equipos…",
  "teams.loadFailed": "No se han podido leer los equipos.",
  "teams.retry": "Reintentar",
  "teams.status.open": "Abierto",
  "teams.status.closed": "Cerrado",
  "teams.rename": "Renombrar",
  "teams.close": "Cerrar el equipo",
  "teams.reopen": "Reabrirlo",
  "teams.closedReason": "Cerrado",
  "teams.reopenedReason": "Reabierto",
  "teams.closedNote": "Cerrado. Todo el mundo sigue en él: al reabrirlo, siguen ahí.",
  "teams.new": "Nuevo equipo",
  "teams.explain":
    "Un grupo de personas que hacen el mismo tipo de trabajo, con un responsable. El trabajo va al equipo, no a una persona: quien esté de turno hoy coge el siguiente. Un equipo no concede nada.",
  "teams.new.people":
    "Quién está en él desde que existe. Marca “Lo dirige” para decir de quién son estas personas: reasigna el trabajo del equipo y sustituye a cualquiera.",
  "teams.new.name": "Nombre",
  "teams.new.purpose": "Para qué sirve",
  "teams.new.create": "Crear el equipo",
  "teams.new.cancel": "Cancelar",
  "teams.new.nameNeeded": "Ponle un nombre al equipo.",
  "teams.nameTaken": "Ya existe un equipo con ese nombre.",
  "teams.created": "Equipo creado.",
  "teams.leadHint": "Son sus personas: reasigna el trabajo del equipo y sustituye a cualquiera.",
  "teams.leadNeedsMember": "Solo se puede dirigir un equipo del que se forma parte.",
  "teams.noLead": "Sin responsable",
  "teams.gone": "Quien se ha ido no puede añadirse a un equipo.",
  "teams.locked": "No puedes modificar los equipos.",
  "screen.rules": "Reglas",
  "screen.hr": "Recursos humanos",
  "sub.hr": "Busca a cualquiera. La selección, los expedientes y todo lo que hay debajo son de recursos humanos.",
  "screen.team": "Roles y equipo",
  "screen.audit": "Auditorías Walliyo",

  // ── Locked screen (opened by URL without the feature) ──
  "lock.requires": "Esta pantalla requiere",
  "lock.or": " o ",
  "lock.roles": "Sus roles ({roles}) no la incluyen, por eso no aparece en su menú.",
  "lock.noRoles": "No tiene ningún rol, por eso no aparece en su menú.",

  // ── Sign-in ──
  "signIn.title": "Acceso a la consola",
  "signIn.subtitle": "Reservado al equipo de Walliyo. Le enviaremos un código por SMS.",
  "signIn.email": "Email profesional",
  "signIn.password": "Contraseña",
  "signIn.submit": "Continuar",
  "signIn.submitting": "Verificando…",
  "verify.title": "Código de verificación",
  "verify.subtitle": "Hemos enviado un código por SMS al {phone}.",
  "verify.code": "Código recibido por SMS",
  "verify.submit": "Iniciar sesión",
  "verify.submitting": "Verificando…",
  "verify.expiresIn": "El código caduca en {time}.",
  "verify.expired": "El código ha caducado. Vuelva a iniciar sesión para recibir uno nuevo.",
  "verify.restart": "Volver a iniciar sesión",

  // ── Errors: what failed and what to do (ADR-0016 §2.5) ──
  "error.requestId": "Identificador de solicitud: {id}",
  "error.noRequestId": "Sin identificador de solicitud: el servidor no respondió.",
  "error.signIn.unauthenticated": "Email o contraseña incorrectos. Compruébelos y vuelva a intentarlo.",
  "error.signIn.validation_error": "Introduzca un email y una contraseña válidos.",
  "error.verify.validation_error": "Introduzca el código de 6 dígitos recibido por SMS.",
  "error.verify.session_expired": "El código ha caducado. Vuelva a iniciar sesión para recibir uno nuevo.",
  "error.verify.unauthenticated": "Esta solicitud de código ya no es válida. Vuelva a iniciar sesión.",
  "error.unauthenticated": "Ya no tiene la sesión iniciada. Vuelva a iniciar sesión.",
  "error.session_expired": "Su sesión ha caducado. Vuelva a iniciar sesión.",
  "error.feature_not_enabled": "Sus roles no permiten esta acción. Pida acceso a un responsable.",
  "error.invalid_code": "Código incorrecto. Revise el SMS e introduzca el código de nuevo.",
  "error.too_many_attempts": "Demasiados intentos. Espere unos minutos y vuelva a iniciar sesión.",
  "error.stale_version": "Esto ha cambiado mientras lo miraba. Recargue para ver la versión actual.",
  "error.validation_error": "Algunos datos no son válidos. Corríjalos y vuelva a intentarlo.",
  "error.internal_error":
    "El servidor ha tenido un error. Vuelva a intentarlo; si continúa, envíe el identificador de abajo al equipo técnico.",
  "error.network_error": "El servidor no responde tras varios intentos. Compruebe su conexión y vuelva a intentarlo.",
  "error.network_error.write":
    "El servidor no responde: puede que la solicitud se haya procesado. Vuelva a intentarlo, no se hará dos veces.",
  "error.unexpected_response":
    "Respuesta inesperada del servidor. Vuelva a intentarlo; si continúa, envíe el identificador de abajo al equipo técnico.",
  "error.unknown":
    "Se ha producido un error inesperado en la consola. Recargue la página; si continúa, avise al equipo técnico.",

  "sub.cases": "Cola compartida · llamadas, emails y chat en un mismo lugar",

  "sub.customers": "Buscar un cliente o un pro y ver toda su cuenta",

  "sub.verifications": "Verificar la identidad de los pros antes de que se les pueda encontrar",

  "sub.reports": "Denuncias sobre pros o clientes, por gravedad",

  "sub.billing":
    "Precios por país, promociones y descuentos · el profesional conserva el precio con el que se suscribió",
  "sub.payments": "Tarifas de los pros · Orange Money y tarjeta",

  "sub.features": "Activar o desactivar por nivel: todo el mundo, una región, una versión, una persona",

  "sub.rules": "Valores de la empresa · cada cambio crea una versión",

  // ── Fonctionnalités ──
  "features.title": "Activaciones de funcionalidades",
  "features.loading": "Cargando las funciones…",
  "features.loadFailed": "No se han podido cargar las funciones.",
  "features.retry": "Reintentar",
  "features.close": "Cerrar",
  "features.pick": "Elija una función para ver sus excepciones.",
  "features.notFound": "Esta función no existe o ya no existe. Elija una de la lista.",
  "features.subject.for": "Registro",
  "features.subject.allClients": "Todos los clientes",
  "features.subject.allPros": "Todos los pros",
  "features.subject.user": "Usuario",
  "features.subject.uuid": "UUID",
  "features.subject.uuidHint": "31290388",
  "features.subject.unknown": "UUID no encontrado.",
  "features.kind.client": "Cliente",
  "features.kind.pro": "Pro",
  "features.col.source": "Origen",
  "features.col.value": "Valor",
  "features.col.category": "Categoría",
  "features.value.on": "Activado",
  "features.value.off": "Desactivado",
  "features.source.default": "Predeterminado",
  "features.source.thisUser": "Este usuario",
  "features.source.user": "Un usuario",
  "features.source.allClients": "Todos los clientes",
  "features.source.allPros": "Todos los pros",
  "features.source.proProfile": "Perfil pro",
  "features.source.region": "Región",
  "features.source.appVersion": "Versión de la app",
  "features.source.system": "Sistema",
  "features.locked.owner": "Solo un Owner puede cambiarlo",
  "features.locked.scope": "No se puede fijar en este nivel",
  "features.save.discard": "Descartar",
  "features.save.sending": "Guardando…",
  "features.save.nothing": "Nada que guardar: ningún cambio.",
  "features.save.locked": "Uno de los cambios no le corresponde.",
  "features.save.reasonNeeded": "Diga por qué, en pocas palabras.",
  "features.save.leave": "Tiene cambios sin guardar.",
  "features.clear": "Volver al valor heredado",
  "features.cleared": "Se borrará",
  "features.filter.any": "Todas",
  "features.filter.own": "Fijada aquí",
  "features.filter.inherited": "Heredada",
  "features.filter.search": "Buscar",
  "features.filter.none": "Ninguna funcionalidad coincide con estos filtros.",
  "features.surface.app": "App Walliyo",
  "features.surface.console": "Consola Walliyo",
  "features.save.title.one": "1 cambio sin guardar",
  "features.save.title.many": "{count} cambios sin guardar",
  "features.save.submit.one": "Guardar 1 cambio",
  "features.save.submit.many": "Guardar {count} cambios",
  "features.save.done.one": "1 cambio guardado.",
  "features.save.done.many": "{count} cambios guardados.",
  "features.unsaved": "sin guardar",
  "features.surface.consoleSoon":
    "Las {count} funciones de la consola solo se pueden fijar por rol por ahora. Por persona llega aquí.",
  "features.surface.consoleWhere": "Abrir Roles y equipo",
  "features.subject.isStaff":
    "Ese número pertenece a un empleado de Walliyo. Sus funciones están en la pestaña Consola Walliyo.",
  "features.subject.search": "Buscar",
  "features.subject.searching": "Buscando…",
  "features.subject.needsUuid": "Escriba las ocho cifras del UUID.",
  "features.subject.pickFirst": "Elija para quién y luego busque.",
  "features.subject.stale": "Estos resultados son de su búsqueda anterior. Busque de nuevo para actualizarlos.",
  "features.pill.allClients": "Todos los registros de cliente",
  "features.pill.allPros": "Todos los registros de pro",
  "features.pill.unknown": "Usuario desconocido",
  "features.pill.staff": "Empleado de Walliyo",
  "features.subject.allStaff": "Todo el personal",
  "features.subject.staffMember": "Empleado",
  "features.staffSource.override": "Excepción",
  "features.staffSource.byRole": "Por rol",
  "features.staffSource.byRoleNamed": "Por rol: {roles}",
  "features.staffSource.allStaff": "Todo el personal",
  "features.locked.ownAccount": "No puede cambiar su propia cuenta",
  "features.locked.grant": "No puede conceder esta",
  "features.pill.allStaff": "Todos los registros de personal",
  "features.pill.unknownStaff": "Empleado desconocido",
  "features.subject.isCustomer":
    "Ese número pertenece a un cliente, no a un empleado. Sus funciones están en la pestaña App Walliyo.",
  "features.subject.employeeUuid": "UUID del empleado",
  "features.subject.staffUuidHint": "27217102",
  "features.col.feature": "Función",
  "features.col.default": "Por defecto",
  "features.col.exceptions": "Excepciones",
  "features.for.clients": "Para clientes",
  "features.for.pros": "Para profesionales",
  "features.for.everyone": "Para todos",
  "features.on": "Activada",
  "features.off": "Desactivada",
  "features.planned": "Prevista",
  "features.planned.note": "Prevista: se puede fijar un valor, pero ningún código lo lee todavía.",
  "features.detail.default": "Por defecto: {value}",
  "features.sensitivity.open":
    "Sensibilidad {level}: cualquier persona que gestione las funciones de la app puede cambiarla.",
  "features.sensitivity.critical":
    "Sensibilidad crítica: solo un propietario puede cambiarla, y se avisa a los propietarios.",
  "features.locked.critical":
    "Bloqueada: esta función es crítica y solo un propietario puede cambiarla. Sus roles ({roles}) no le convierten en propietario; el servidor también lo rechazaría.",
  "features.exceptions.title": "Excepciones por nivel",
  "features.col.scope": "Nivel",
  "features.col.target": "Destino",
  "features.col.reason": "Motivo",
  "features.col.expires": "Caduca",
  "features.col.actions": "Gestionar",
  "features.default.scope": "Por defecto",
  "features.default.reason": "valor por defecto",
  "features.exceptions.none": "Sin excepciones: el valor por defecto se aplica a todos.",
  "features.expired": "Caducada",
  "features.expires.never": "Hasta que se cambie",
  "features.edit": "Modificar",
  "features.remove": "Quitar",
  "features.add": "Añadir una excepción",
  "features.scope.system": "Sistema",
  "features.scope.allClients": "Todos los clientes",
  "features.scope.allPros": "Todos los profesionales",
  "features.scope.region": "Región",
  "features.scope.appVersion": "Versión de la app",
  "features.scope.user": "Una persona",
  "features.scope.proProfile": "Un perfil de profesional",
  "features.scope.clientProfile": "Un perfil de cliente",
  "features.target.everyone": "Todos",
  "features.target.allClients": "Todos los clientes",
  "features.target.allPros": "Todos los profesionales",
  "features.target.appVersion": "Versión {version} y posteriores",
  "features.target.unnamed": "Cuenta sin nombre",
  "features.form.addTitle": "Nueva excepción",
  "features.form.editTitle": "Modificar la excepción",
  "features.form.scope": "Nivel",
  "features.form.scopePick": "Elegir un nivel…",
  "features.form.scopeUnsupported": "{scope} (todavía no es posible)",
  "features.form.clientProfileNote":
    "Un perfil de cliente aún no puede recibir un valor: los perfiles de cliente todavía no existen. Para un cliente, elija «Una persona».",
  "features.form.needsSearch": "Elegir una persona o un perfil de profesional requiere",
  "features.form.needsView": "Encontrar el perfil de profesional de una cuenta requiere",
  "features.form.region": "Región",
  "features.form.regionPick": "Elegir una región…",
  "features.form.appVersion": "A partir de la versión",
  "features.form.appVersionHint": "Por ejemplo 2.4.0: se aplica a esa versión y a las posteriores.",
  "features.form.person": "Persona",
  "features.form.pro": "Perfil de profesional",
  "features.form.search": "Buscar",
  "features.form.searching": "Buscando…",
  "features.form.searchPlaceholder": "Nombre, teléfono, email o ID de cuenta",
  "features.form.noResults": "Ninguna cuenta coincide.",
  "features.form.pickedChange": "Cambiar",
  "features.form.value": "Valor",
  "features.form.reason": "Motivo (obligatorio)",
  "features.form.reasonHint": "Se registra en el registro de auditoría con el cambio.",
  "features.form.expires": "Caduca el",
  "features.form.expiresHint": "Opcional. Vacío: hasta que se cambie.",
  "features.form.version": "Basada en la versión {version} de esta excepción.",
  "features.form.submitAdd": "Añadir la excepción",
  "features.form.submitEdit": "Guardar",
  "features.form.cancel": "Cancelar",
  "features.form.sending": "Enviando…",
  "features.problem.scope": "Elija uno de los niveles propuestos.",
  "features.problem.region": "Elija una región.",
  "features.problem.appVersion": "Introduzca una versión como 2.4.0.",
  "features.problem.person": "Busque a la persona y elíjala en los resultados.",
  "features.problem.pro": "Busque al profesional y elija una cuenta con perfil de profesional.",
  "features.problem.exists": "Ya existe una excepción para este destino: modifíquela.",
  "features.problem.reason": "Indique un motivo en pocas palabras.",
  "features.problem.expiryInvalid": "Esta fecha de caducidad no es válida.",
  "features.problem.expiryPast": "La fecha de caducidad debe ser futura, o déjela vacía.",
  "features.refused.critical": "Rechazado: esta función es crítica, solo un propietario puede cambiarla.",
  "features.refused.scopeNotSupported":
    "Rechazado: un perfil de cliente aún no puede recibir un valor, los perfiles de cliente todavía no existen. Elija «Una persona».",
  "features.refused.scopeNotAllowed":
    "Rechazado: esta función no se puede fijar en ese nivel. Elija uno de los niveles propuestos.",
  "features.refused.expiryInPast":
    "Rechazado: la fecha de caducidad ya pasó. Elija una fecha posterior o déjela vacía.",
  "features.refused.targetNotFound": "Rechazado: esa cuenta o perfil de profesional ya no existe. Búsquela de nuevo.",
  "features.stale":
    "Alguien ha cambiado esta función mientras tanto. Recargue para ver sus excepciones actuales y vuelva a intentarlo.",
  "features.done.added": "Excepción añadida.",
  "features.done.saved": "Excepción modificada.",
  "features.done.removed": "Excepción quitada: vuelve a aplicarse el nivel más amplio o el valor por defecto.",
  "features.remove.title": "Quitar la excepción",
  "features.remove.body":
    "Quitar la excepción «{scope} · {target}: {value}» de {feature}. Volverá a aplicarse el nivel más amplio o el valor por defecto.",
  "features.remove.submit": "Quitar",

  // ── Règles ──
  "rules.loading": "Cargando las reglas…",
  "rules.loadFailed": "No se han podido cargar las reglas.",
  "rules.retry": "Reintentar",
  "rules.pick": "Elija una regla para ver su historial.",
  "rules.notFound": "Esta regla no existe. Elija una de la lista.",
  "rules.next": "a partir del {date}: {value}",
  "rules.now": "En vigor",
  "rules.default": "valor por defecto",
  "rules.kind.gnf": "importe en GNF",
  "rules.kind.minutes": "duración en minutos",
  "rules.kind.hours": "duración en horas",
  "rules.kind.days": "duración en días",
  "rules.kind.km": "distancia en km",
  "rules.kind.number": "número",
  "rules.kind.boolean": "sí o no",
  "rules.kind.choice": "elección de una lista",
  "rules.kind.text": "texto",
  "rules.sensitivity.open":
    "Sensibilidad {level}: cualquier persona que pueda modificar las reglas puede crear una versión.",
  "rules.sensitivity.critical": "Sensibilidad crítica: solo un propietario puede crear una versión.",
  "rules.legalBasis.required": "Cada cambio debe citar su base legal.",
  "rules.locked.critical":
    "Bloqueada: esta regla es crítica, solo un propietario puede crear una versión. Sus roles ({roles}) no le convierten en propietario; el servidor también lo rechazaría.",
  "rules.locked.retired": "Esta regla está retirada: ya nada la lee, así que no puede cambiar.",
  "rules.history": "Historial de versiones",
  "rules.history.none": "Ninguna versión: se aplica el valor por defecto ({value}).",
  "rules.col.version": "N.º de versión",
  "rules.col.value": "Valor",
  "rules.col.effective": "En vigor",
  "rules.col.reason": "Motivo",
  "rules.col.legalBasis": "Base legal",
  "rules.col.by": "Por",
  "rules.status.scheduled": "A partir del {date}",
  "rules.status.current": "Actual",
  "rules.status.since": "desde el {date}",
  "rules.status.past": "Sustituida",
  "rules.new": "Nueva versión",
  "rules.form.title": "Nueva versión",
  "rules.form.value": "Nuevo valor",
  "rules.form.value.gnf": "Nuevo importe (GNF)",
  "rules.form.value.minutes": "Nuevo valor (en minutos)",
  "rules.form.value.hours": "Nuevo valor (en horas)",
  "rules.form.value.days": "Nuevo valor (en días)",
  "rules.form.effective": "Entrada en vigor",
  "rules.form.now": "Ahora",
  "rules.form.at": "En una fecha y hora",
  "rules.form.atLabel": "En vigor el",
  "rules.form.reason": "Motivo (obligatorio)",
  "rules.form.legalBasis": "Base legal (obligatoria)",
  "rules.form.legalBasisOptional": "Base legal (si procede)",
  "rules.form.legalBasisPlaceholder": "Ley, contrato, dictamen jurídico",
  "rules.form.version":
    "Basada en la versión {version} de la regla: si mientras tanto se añade otra versión, se rechazará.",
  "rules.form.submit": "Crear la versión",
  "rules.form.cancel": "Cancelar",
  "rules.form.sending": "Enviando…",
  "rules.value.true": "Sí",
  "rules.value.false": "No",
  "rules.problem.value": "Introduzca un valor.",
  "rules.problem.number": "Introduzca un número, por ejemplo 750000.",
  "rules.problem.integer": "Introduzca un número entero.",
  "rules.problem.min": "El valor debe ser al menos {min}.",
  "rules.problem.max": "El valor debe ser como máximo {max}.",
  "rules.problem.choice": "Elija un valor de la lista.",
  "rules.problem.tooLong": "Como máximo {max} caracteres.",
  "rules.problem.effectiveMissing": "Elija la fecha y la hora de entrada en vigor.",
  "rules.problem.effectivePast":
    "Una versión no puede entrar en vigor en el pasado: lo que se aplicaba entonces queda registrado. Elija ahora o más tarde.",
  "rules.problem.reason": "Indique un motivo en pocas palabras.",
  "rules.problem.legalBasis": "Cite la ley, el contrato o el dictamen jurídico que respalda este cambio.",
  "rules.refused.critical": "Rechazado: esta regla es crítica, solo un propietario puede crear una versión.",
  "rules.refused.effectivePast": "Rechazado: esa fecha ya pasó. Elija ahora o más tarde.",
  "rules.refused.legalBasis":
    "Rechazado: esta regla exige una base legal. Cite la ley, el contrato o el dictamen jurídico.",
  "rules.refused.invalidValue": "Rechazado: ese valor no encaja en la regla. Compruebe la unidad y el mínimo.",
  "rules.refused.retired": "Rechazado: esta regla está retirada, ya nada la lee.",
  "rules.stale": "Mientras tanto se ha añadido otra versión de esta regla. Recargue para verla y vuelva a intentarlo.",
  "rules.done.now": "Versión {version} creada: se aplica desde ahora.",
  "rules.done.scheduled": "Versión {version} creada: se aplicará a partir del {date}.",

  "sub.team": "Cada rol es una lista de funciones. Nadie puede concederse nada a sí mismo.",

  "sub.audit": "Todo lo que se ha hecho, por quién, para quién · su consulta también queda registrada",

  // ── Audit log ──
  "audit.filter.subject": "Persona afectada",
  "audit.filter.subjectPlaceholder": "Identificador de la cuenta",
  "audit.filter.staff": "Miembro del equipo",
  "audit.filter.staffPlaceholder": "Identificador del miembro",
  "audit.filter.action": "Acción",
  "audit.filter.area": "Ámbito",
  "audit.filter.outcome": "Resultado",
  "audit.filter.from": "Desde",
  "audit.filter.to": "Hasta (incluido)",
  "audit.filter.all": "Todos",
  "audit.filter.allActions": "Todas",
  "audit.filter.clear": "Borrar filtros",
  "audit.problem.id": "Un identificador tiene la forma 3f2a…-…-…-…-… (UUID). Cópielo desde la ficha de la cuenta.",
  "audit.problem.period": "El final del periodo es anterior a su inicio.",
  "audit.problem.needsPerson": "Indique el UUID de quien cambió, el de la persona, o ambos.",
  "audit.filtersAria": "Filtros del registro",
  "audit.searchAria": "Búsqueda en el registro",
  "audit.scope.user": "Individuo",
  "audit.scope.allStaff": "Todo el personal",
  "audit.scope.allClients": "Todos los clientes",
  "audit.scope.allPros": "Todos los pros",
  "audit.scope.staffRole": "Rol",
  "audit.scope.proProfile": "Perfil pro",
  "audit.scope.region": "Región",
  "audit.scope.appVersion": "Versión de la app",
  "audit.scope.system": "Sistema",
  "audit.scope.staffMember": "Individuo",
  "audit.filter.involving": "Implicando",
  "audit.filter.updaterUuid": "UUID de quien cambió",
  "audit.filter.userUuid": "UUID de la persona",
  "audit.pill.updater": "Autor",
  "audit.pill.user": "Persona",
  "audit.col.time": "Hora",
  "audit.col.actor": "Autor",
  "audit.col.action": "Ajuste",
  "audit.col.subject": "Afecta a",
  "audit.col.outcome": "Resultado",
  "audit.col.reason": "Motivo",
  "audit.filter.effect": "Mostrar",
  "audit.effect.write": "Cambios",
  "audit.effect.read": "Consultas",
  "audit.col.before": "Antes",
  "audit.col.after": "Después",
  "audit.change.arrow": "{before} → {after}",
  "audit.change.field": "{key}: {value}",
  "audit.outcome.succeeded": "Correcto",
  "audit.outcome.denied": "Denegado",
  "audit.outcome.requested": "Solicitado",
  "audit.actor.staff": "Miembro del equipo",
  "audit.actor.noRoles": "sin rol",
  "audit.actor.customer": "cliente",
  "audit.actor.self": "cliente (él mismo)",
  "audit.actor.system": "Sistema",
  "audit.subject.onBehalf": "en nombre de {id}",
  "audit.filterBy": "Filtrar por {id}",
  "audit.subject.open": "Abrir la cuenta de {name}",
  "audit.masked": "oculto",
  "audit.sensitive": "🔒 sensible",
  "audit.loading": "Cargando el registro…",
  "audit.loadFailed": "No se pudo cargar el registro.",
  "audit.retry": "Reintentar",
  "audit.empty": "Ninguna entrada coincide con estos filtros.",
  "audit.emptyForPerson": "Ninguna entrada para esta búsqueda.",
  "audit.fixProblems": "Corrija los filtros señalados para mostrar el registro.",
  "audit.loadMore": "Cargar más",
  "audit.loadingMore": "Cargando…",
  "audit.moreFailed": "No se pudo cargar la página siguiente. Las entradas ya mostradas siguen siendo válidas.",
  "audit.count": "{count} entradas mostradas",
  "audit.countOne": "1 entrada mostrada",
  "audit.end": "Fin del registro para estos filtros.",
  "audit.recorded": "Su consulta también queda registrada (VIEW_AUDIT_LOG_STAFF_ACTION), con los filtros utilizados.",
  "audit.sensitiveShown": "Está viendo los valores sensibles (VIEW_SENSITIVE_AUDIT_VALUES).",
  "audit.sensitiveHidden":
    "Los valores sensibles están ocultos: se necesita VIEW_SENSITIVE_AUDIT_VALUES, que solo concede el propietario.",
  "audit.deniedNote": "Una línea «Denegado» muestra que también se registra un intento sin permiso.",
  "area.account": "Cuenta",
  "area.access": "Acceso",
  "area.onboarding": "Alta de pros",
  "area.discovery": "Búsqueda",
  "area.jobs": "Solicitudes",
  "area.leads": "Oportunidades",
  "area.messaging": "Mensajes",
  "area.reviews": "Reseñas",
  "area.payments": "Pagos",
  "area.support": "Expedientes",
  "area.safety": "Seguridad",
  "area.settings": "Ajustes",
  "area.staff": "Equipo",
  "area.audit": "Auditoría",

  // ── Roles & team ──
  "team.members": "Equipo",
  "team.col.name": "Nombre",
  "team.col.roles": "Roles",
  "team.col.status": "Estado",
  "team.col.lastSeen": "Última conexión",
  "team.you": "usted",
  "team.noRoles": "Sin rol",
  "team.status.active": "Activo",
  "team.status.invited": "Invitado",
  "team.status.deactivated": "Desactivado",
  "team.sessions": "{count} sesiones abiertas",
  "team.sessionsOne": "1 sesión abierta",
  "team.lastSeen.never": "Nunca conectado",
  "team.lastSeen.today": "hoy a las {time}",
  "team.lastSeen.yesterday": "ayer a las {time}",
  "team.lastSeen.date": "el {date}",
  "team.invite": "Invitar",
  "team.manageRoles": "Roles",
  "team.endSessions": "Cerrar la sesión",
  "team.deactivate": "Desactivar",
  "team.loading": "Cargando el equipo…",
  "team.loadFailed": "No se pudo cargar el equipo.",
  "team.retry": "Reintentar",
  "team.emptyStaff": "Todavía no hay nadie en el equipo.",
  "team.grid": "Roles × funciones",
  "team.grid.feature": "Función",
  "team.grid.editable": "Haga clic para conceder o retirar · «critical» requiere al propietario",
  "team.grid.readOnly": "🔒 Solo lectura · para modificar se necesita MANAGE_ROLES o ASSIGN_ROLES",
  "team.grid.members": "{count} miembros",
  "team.grid.membersOne": "1 miembro",
  "team.grid.yourRole": "🔒 su rol",
  "team.grid.newRole": "Nuevo rol",
  "team.grid.editRole": "Modificar",
  "team.grid.noRoles": "Todavía no existe ningún rol.",
  "team.lock.ownRole": "Usted tiene este rol: nadie modifica lo que concede su propio rol.",
  "team.lock.critical": "Crítica: solo el propietario puede concederla o retirarla.",
  "team.lock.high": "Sensibilidad alta: se necesita MANAGE_ROLES y tener uno mismo esta función.",
  "team.lock.low": "Se necesita MANAGE_ROLES o ASSIGN_ROLES.",
  "team.lock.criticalShort": "🔒 propietario",
  "team.lock.highShort": "🔒 fuera de sus permisos",
  "team.cell.grant": "Conceder {feature} al rol {role}",
  "team.cell.revoke": "Retirar {feature} del rol {role}",
  "team.sensitivity.low": "low",
  "team.sensitivity.high": "high",
  "team.sensitivity.critical": "critical",

  "team.reason": "Motivo",
  "team.reasonHint": "Se guarda en el registro de auditoría. Bastan unas palabras.",
  "team.reasonTooShort": "Indique un motivo de al menos 3 caracteres.",
  "team.cancel": "Cancelar",
  "team.close": "Cerrar",
  "team.sending": "Enviando…",
  "team.reload": "Recargar",
  "team.refused": "Denegado: {message}",
  "team.stale": "Alguien ha modificado esto mientras tanto. Recargue para ver la versión actual y vuelva a empezar.",

  "team.grant.title": "Conceder una función",
  "team.revoke.title": "Retirar una función",
  "team.grant.body": "Conceder {feature} al rol {role} ({members}).",
  "team.revoke.body": "Retirar {feature} del rol {role} ({members}).",
  "team.grant.submit": "Conceder",
  "team.revoke.submit": "Retirar",
  "team.grant.done": "{feature} concedida al rol {role}.",
  "team.revoke.done": "{feature} retirada del rol {role}.",
  "team.critical.note": "Función crítica: se avisará a los propietarios.",

  "team.invite.title": "Invitar a un miembro del equipo",
  "team.invite.name": "Nombre completo",
  "team.invite.email": "Email profesional",
  "team.invite.phone": "Teléfono",
  "team.invite.phoneHint": "Formato internacional, p. ej. +224621234567.",
  "team.invite.roles": "Roles",
  "team.invite.noRoles": "Cree primero un rol — puede invitar ahora y asignar roles después.",
  "team.invite.rolesHint": "Sin rol, abrirá una consola donde no hay nada accesible.",
  "team.invite.sms":
    "Se enviará un código de 6 dígitos por SMS a este número. Válido durante {hours} horas, permite elegir la contraseña en la página «Elegir mi contraseña».",
  "team.invite.submit": "Enviar la invitación",
  "team.invite.done": "{name} ha sido invitado. Código enviado por SMS, válido hasta el {until}.",
  "team.invite.missing": "Indique el nombre, el email y el teléfono.",

  "team.deactivate.title": "Desactivar a {name}",
  "team.deactivate.body":
    "{name} no podrá volver a iniciar sesión. Sus sesiones abiertas y su posible invitación terminan de inmediato. Un miembro desactivado no se elimina.",
  "team.deactivate.submit": "Desactivar",
  "team.deactivate.done": "{name} ha sido desactivado. Sesiones cerradas: {count}.",
  "team.reactivate": "Reactivar",
  "team.reactivate.title": "Reactivar a {name}",
  "team.reactivate.body":
    "{name} podrá iniciar sesión de nuevo, con los roles que ya tenía. El acceso empieza de cero: no se restaura ninguna sesión ni código.",
  "team.reactivate.submit": "Reactivar",
  "team.reactivate.done": "{name} vuelve a estar activo.",

  "team.endSessions.title": "Cerrar las sesiones de {name}",
  "team.endSessions.body": "{name} se desconectará en todas partes ({sessions}).",
  "team.endSessions.self": "Son sus propias sesiones: usted también se desconectará.",
  "team.endSessions.submit": "Cerrar",
  "team.endSessions.done": "Sesiones de {name} cerradas: {count}.",

  "team.roles.title": "Roles de {name}",
  "team.roles.held": "Roles actuales",
  "team.roles.available": "Otros roles",
  "team.roles.none": "Ninguno",
  "team.roles.assign": "Asignar {role}",
  "team.roles.remove": "Retirar {role}",
  "team.roles.assignDone": "{role} asignado a {name}.",
  "team.roles.removeDone": "{role} retirado a {name}.",
  "team.roles.reasonFirst": "Indique primero un motivo y luego elija el rol que desea asignar o retirar.",

  "team.role.createTitle": "Nuevo rol",
  "team.role.editTitle": "Modificar el rol {name}",
  "team.role.name": "Nombre del rol",
  "team.role.description": "Descripción",
  "team.role.descriptionHint": "Opcional. Lo que hace este rol, en una frase.",
  "team.role.department": "Departamento",
  "team.role.noDepartment": "Ninguno",
  "team.role.departmentHint":
    "El espacio al que llegan sus titulares. Lo que el perfil permite lo siguen decidiendo las funcionalidades.",
  "team.role.create": "Crear el rol",
  "team.role.save": "Guardar",
  "team.role.created": "Rol {name} creado. Concédale funciones en la tabla.",
  "team.role.saved": "Rol {name} guardado.",
  "team.role.nameTooShort": "El nombre debe tener al menos 2 caracteres.",
  "team.role.unchanged": "Modifique el nombre o la descripción.",
  "team.role.version": "Versión {version}",

  // ── Permissions on a role (ADR-0019) and who can do this ──
  "team.tab.roles": "Roles y equipo",
  "team.tab.who": "Quién puede hacerlo",
  "team.tabs.aria": "Secciones del equipo",
  "team.permission.noLimit": "Sin límite",
  "team.permission.lockFeature": "Conceda primero {feature} a {role}: un permiso solo cuenta bajo su función.",
  "team.cell.grantPermission": "Conceder {permission} al rol {role}",
  "team.cell.editPermission": "Cambiar {permission} para el rol {role}",
  "team.permission.grantTitle": "Conceder un permiso",
  "team.permission.editTitle": "Cambiar un permiso",
  "team.permission.grantBody": "Conceder {permission} al rol {role} ({members}).",
  "team.permission.editBody": "El rol {role} ({members}) tiene {permission}.",
  "team.permission.under": "Bajo {feature}.",
  "team.permission.approvable": "Sin él, su intento se convierte en una solicitud de aprobación.",
  "team.permission.refusable": "Sin él, la acción se rechaza.",
  "team.permission.limits": "Límites",
  "team.permission.limitsHint":
    "El máximo que sus titulares pueden hacer solos en cada moneda. Por encima, o en una moneda sin límite, se convierte en una solicitud de aprobación.",
  "team.permission.noLimitHint": "Sin límite: sus titulares actúan solos, sea cual sea el importe.",
  "team.permission.currency": "Moneda",
  "team.permission.amount": "Importe",
  "team.permission.addLimit": "Añadir un límite en otra moneda",
  "team.permission.removeLimit": "Quitar el límite en {currency}",
  "team.permission.limitProblem": "Indique una moneda de tres letras y un importe superior a cero para cada límite.",
  "team.permission.duplicateCurrency": "Un límite por moneda: {currency} aparece dos veces.",
  "team.permission.grantDone": "{permission} concedido al rol {role}.",
  "team.permission.revokeDone": "{permission} retirado al rol {role}.",
  "team.revoke.permissionsToo": "Sus permisos se van con ella: {permissions}.",
  "team.refused.sensitivity":
    "Su acceso no permite concederlo ni retirarlo. Pídaselo a un Ops Manager o al propietario.",
  "team.refused.critical": "Crítico: solo un propietario que lo tenga lo concede o lo retira.",
  "team.refused.limitAboveOwn": "No puede conceder un límite superior al suyo. Pídaselo al propietario.",
  "team.refused.featureFirst":
    "Este permiso se apoya en una función que el rol no concede. Conceda primero la función.",
  "team.refused.notLimited": "Este permiso no lleva límite.",
  "team.refused.limitWithoutGrant": "Un límite va con un permiso concedido.",
  "team.refused.ownRole": "Tiene este rol, así que no puede cambiar lo que concede. Pídaselo a otro propietario.",
  "team.who.action": "Acción",
  "team.who.feature": "Función",
  "team.who.permission": "Permiso",
  "team.who.noPermission": "Ningún permiso cubre esta acción: quien tiene la función puede realizarla.",
  "team.who.approvable": "puede solicitarse",
  "team.who.col.through": "A través de",
  "team.who.col.limit": "Límite",
  "team.who.personal": "Definido para esta persona",
  "team.who.mayRequest": "Solo puede pedirlo: se convierte en una solicitud de aprobación",
  "team.who.loading": "Cargando quién puede hacerlo…",
  "team.who.loadFailed": "No se ha podido cargar quién puede hacerlo.",
  "team.who.empty": "Nadie puede hacerlo.",

  // ── Customers & pros ──
  // Trades, by WorkerCategory.
  "trade.electrician": "Electricista",
  "trade.plumber": "Fontanero",
  "trade.mason": "Albañil",
  "trade.carpenter": "Carpintero",
  "trade.ac_technician": "Técnico de aire acondicionado",
  "trade.cleaner": "Limpiadora",
  "trade.painter": "Pintor",
  "trade.handyman": "Manitas",
  "trade.laundry": "Lavandería",
  "trade.babysitter": "Canguro",
  "trade.fridge_repair": "Reparador de neveras",
  "trade.moto_driver": "Mototaxi",
  "trade.tailor": "Sastre",
  "trade.car_mechanic": "Mecánico de coches",
  "trade.welder": "Soldador",
  "trade.gardener": "Jardinero",
  "trade.phone_repair": "Reparador de teléfonos",
  "trade.tutor": "Profesor particular",
  "trade.hairdresser": "Peluquero",
  "trade.security_guard": "Vigilante",
  "trade.other": "Otro",
  // A number with its unit (the settings' Unit).
  "unit.km": "{value} km",
  "unit.minutes": "{value} min",
  "unit.hours": "{value} h",
  "unit.days": "{value} d",
  "unit.seconds": "{value} s",

  "customers.search.label": "Buscar un cliente o un pro",
  "customers.search.placeholder": "Nombre, teléfono, email o identificador de la cuenta",
  "customers.search.hint": "P. ej. «621 23 45 67», «Sékou Touré», «mariama.bah@» o el identificador de la cuenta.",
  "customers.search.submit": "Buscar",
  "customers.search.tooShort": "Introduzca al menos 2 caracteres.",
  "customers.search.locked": "La búsqueda requiere",
  "customers.search.idle": "Introduzca un nombre, un número, un email o un identificador para encontrar una cuenta.",
  "customers.search.loading": "Buscando…",
  "customers.search.loadFailed": "No se pudo completar la búsqueda.",
  "customers.search.empty": "Ninguna cuenta coincide con «{query}».",
  "customers.search.matchedBy.userId": "Búsqueda por identificador de cuenta",
  "customers.search.matchedBy.email": "Búsqueda por email",
  "customers.search.matchedBy.phone": "Búsqueda por teléfono",
  "customers.search.matchedByPhone": "Búsqueda por teléfono: {phone}",
  "customers.search.matchedBy.name": "Búsqueda por nombre",
  "customers.search.count": "{count} cuentas mostradas",
  "customers.search.countOne": "1 cuenta mostrada",
  "customers.search.end": "Fin de los resultados.",
  "customers.search.loadMore": "Cargar más",
  "customers.search.loadingMore": "Cargando…",
  "customers.search.moreFailed":
    "No se pudo cargar la página siguiente. Las cuentas ya mostradas siguen siendo válidas.",
  "customers.search.retry": "Reintentar",
  "customers.search.masked":
    "Números y emails ocultos. «Mostrar», en la página de la cuenta y con un motivo, queda registrado.",
  "customers.col.name": "Nombre",
  "customers.col.contact": "Contacto",
  "customers.col.kind": "Cliente o pro",
  "customers.col.region": "Región",
  "customers.col.since": "Miembro desde",
  "customers.deleted": "Cuenta eliminada",
  "customers.deletedName": "Nombre borrado",
  "customers.kind.client": "Cliente",
  "customers.kind.pro": "Pro",
  "customers.kind.proAndClient": "Pro y cliente",
  "customers.kind.admin": "Administrador",
  "customers.region.gn": "Guinea",
  "customers.region.sn": "Senegal",
  "customers.region.us": "Estados Unidos",
  "customers.region.eu": "Europa",
  "customers.verification.unverified": "No verificada",
  "customers.verification.pending": "Pendiente",
  "customers.verification.verified": "Verificada",
  "customers.verification.rejected": "Rechazada",

  "customers.back": "← Búsqueda",
  "customers.page.loading": "Cargando la cuenta…",
  "customers.page.loadFailed": "No se pudo cargar la cuenta.",
  "customers.page.notFound": "Ninguna cuenta tiene este identificador. Vuelva a buscar a la persona.",
  "customers.page.locked": "La página de una cuenta requiere",
  "customers.page.sub": "{kind} · miembro desde {since} · {region}",
  "customers.page.deletedTitle": "Cuenta eliminada",
  "customers.page.deletedSub": "Eliminada el {date} · nombre y datos de contacto borrados",

  "customers.reveal.button": "Mostrar",
  "customers.reveal.locked": "🔒 oculto",
  "customers.reveal.lockedTitle":
    "Mostrar los datos de contacto requiere REVEAL_CONTACT_DETAILS_STAFF_FEATURE_ENABLEMENT",
  "customers.reveal.title": "Mostrar los datos de contacto de {name}",
  "customers.reveal.body":
    "El número y el email completos se muestran solo en esta página, mientras dure su consulta. La visualización queda registrada en el registro de la cuenta, con su motivo.",
  "customers.reveal.reason": "Motivo",
  "customers.reveal.placeholder": "¿Por qué? p. ej. devolver la llamada al cliente, expediente D-0142",
  "customers.reveal.tooShort": "Indique un motivo de al menos 3 caracteres.",
  "customers.reveal.submit": "Mostrar",
  "customers.reveal.sending": "Enviando…",
  "customers.reveal.cancel": "Cancelar",
  "customers.reveal.noAnswer":
    "El servidor no responde: puede que la visualización se haya registrado sin que le lleguen los datos. Vuelva a intentarlo; la nueva visualización también quedará registrada.",
  "customers.reveal.recorded": "Mostrado a las {time} · consulta registrada en el registro de la cuenta.",
  "customers.reveal.hide": "Ocultar",
  "customers.reveal.phone": "Teléfono:",
  "customers.reveal.email": "Email:",
  "customers.reveal.none": "ninguno",

  "customers.obo.title": "Actuar en nombre de {name}",
  "customers.obo.body":
    "Se envía un código por SMS al número de la cuenta. El cliente lo lee en voz alta; la sesión dura 30 minutos y cada acción queda registrada con su nombre y el suyo.",
  "customers.obo.send": "Enviar el código",
  "customers.obo.soon": "Próximamente",

  "customers.tabs.aria": "Secciones de la cuenta",
  "customers.tab.overview": "Resumen",
  "customers.tab.subscription": "Suscripción",
  "customers.tab.settings": "Funciones y ajustes",
  "customers.tab.jobs": "Solicitudes",
  "customers.tab.audit": "Registro",

  "customers.pro.title": "Perfil pro",
  "customers.pro.none": "Sin perfil pro.",
  "customers.pro.identity": "Identidad",
  "customers.pro.approval": "Aprobación",
  "customers.pro.rating": "Valoración",
  "customers.pro.ratingValue": "{rating} ★ · {count} reseñas",
  "customers.pro.noReviews": "Sin reseñas",
  "customers.pro.rate": "Tarifa",
  "customers.pro.subscription": "Plan",
  "customers.pro.noSubscription": "Ninguno",
  "customers.pro.subscriptionLine": "{plan} · {status}",
  "customers.pro.paidUntil": "{plan} · pagado hasta el {date}",
  "customers.pro.endingUntil": "{plan} · activa hasta el {date}, sin renovación",
  "customers.pro.approval.pending": "Pendiente",
  "customers.pro.approval.approved": "Aprobado",
  "customers.pro.approval.rejected": "Rechazado",
  "customers.subscription.pending": "pendiente",
  "customers.subscription.active": "activo",
  "customers.subscription.ending": "sin renovación",
  "customers.subscription.suspended": "suspendida",
  "customers.subscription.ended": "finalizada",
  "customers.rate.hourly": "{amount} / hora",
  "customers.rate.daily": "{amount} / día",
  "customers.rate.fixed": "{amount} precio cerrado",
  "customers.client.title": "Perfil cliente",
  "customers.client.jobsPosted": "Solicitudes publicadas",
  "customers.client.lastActivity": "Última actividad",
  "customers.client.never": "Ninguna",
  "customers.restrictions.title": "Restricciones activas",
  "customers.restrictions.none": "Ninguna restricción.",
  "customers.restrictions.suspension": "Suspensión provisional",
  "customers.restrictions.line": "{feature} desactivada para {scope} · motivo: {reason}",
  "customers.restrictions.until": "hasta el {date}",

  "customers.settings.col.setting": "Ajuste",
  "customers.settings.col.value": "Valor",
  "customers.settings.col.source": "Nivel",
  "customers.settings.col.reason": "Motivo",
  "customers.settings.col.expires": "Caduca",
  "customers.settings.on": "Activado",
  "customers.settings.off": "Desactivado",
  "customers.settings.restricted": "Restringido",
  "customers.settings.none": "No se aplica ningún ajuste a esta cuenta.",
  "customers.settings.edit": "Modificar un ajuste",
  "customers.settings.editSoon":
    "Próximamente: la modificación de los ajustes de un cliente llegará en una próxima versión.",
  "customers.settings.foot":
    "Prevalece el nivel más preciso: persona, perfil pro, todos los pros o todos los clientes, región, sistema y, por último, el valor por defecto.",
  "customers.source.user": "Persona",
  "customers.source.proProfile": "Perfil pro",
  "customers.source.allPros": "Todos los pros",
  "customers.source.allClients": "Todos los clientes",
  "customers.source.region": "Región",
  "customers.source.regionOf": "Región {region}",
  "customers.source.appVersion": "Versión de la app",
  "customers.source.system": "Sistema",
  "customers.source.default": "Por defecto",
  "customers.scope.user": "la persona",
  "customers.scope.proProfile": "el perfil pro",

  "customers.jobs.col.trade": "Oficio",
  "customers.jobs.col.type": "Tipo",
  "customers.jobs.col.side": "Papel",
  "customers.jobs.col.other": "Con",
  "customers.jobs.col.status": "Estado",
  "customers.jobs.col.date": "Fecha",
  "customers.jobs.type.direct": "Directa",
  "customers.jobs.type.open": "Abierta",
  "customers.jobs.side.client": "Cliente",
  "customers.jobs.side.pro": "Pro",
  "customers.jobs.status.requested": "Solicitada",
  "customers.jobs.status.quoted": "Presupuesto enviado",
  "customers.jobs.status.accepted": "Presentación hecha",
  "customers.jobs.status.cancelled": "Cancelada",
  "customers.jobs.noPro": "Ningún pro elegido",
  "customers.jobs.deletedParty": "Cuenta eliminada",
  "customers.jobs.none": "Ninguna solicitud para esta cuenta.",
  "customers.jobs.foot": "Como máximo las {count} últimas solicitudes, como cliente o como pro.",

  "customers.journal.recorded": "Su consulta de este registro también queda registrada.",
  "customers.journal.locked": "El registro de auditoría requiere",
  "customers.lockedRoles": "Sus roles ({roles}) no lo incluyen.",
  "customers.journal.onBehalf": "en nombre de {id}",
  "customers.journal.actorWithRoles": "{name} ({roles})",
  "customers.journal.reason": "motivo: {reason}",
  "customers.journal.empty": "Todavía no hay nada registrado para esta cuenta.",
  "customers.journal.all": "Abrir en el registro de auditoría →",
  "customers.journal.denied": "denegado",
  "customers.journal.succeeded": "correcto",

  // ── Choose my password (public) ──
  "setPassword.title": "Elegir mi contraseña",
  "setPassword.subtitle": "Use el código de 6 dígitos recibido por SMS con su invitación.",
  "setPassword.email": "Email profesional",
  "setPassword.code": "Código recibido por SMS",
  "setPassword.password": "Nueva contraseña",
  "setPassword.confirm": "Confirme la contraseña",
  "setPassword.rules": "La contraseña debe:",
  "setPassword.rule.length": "tener al menos {min} caracteres",
  "setPassword.rule.tooLong": "tener como máximo {max} caracteres",
  "setPassword.rule.mix": "contener una letra y un carácter que no sea una letra",
  "setPassword.rule.email": "no contener su email ({name})",
  "setPassword.rule.emailGeneric": "no contener su email",
  "setPassword.mismatch": "Las dos contraseñas no coinciden.",
  "setPassword.codeFormat": "El código tiene 6 dígitos.",
  "setPassword.emailFormat": "Introduzca un email válido.",
  "setPassword.submit": "Guardar mi contraseña",
  "setPassword.submitting": "Guardando…",
  "setPassword.back": "Volver al inicio de sesión",
  "setPassword.done": "Contraseña guardada. Inicie sesión con su email y esta contraseña.",
  "signIn.setPassword": "¿Invitado? Elegir mi contraseña",
  "signIn.or": "o",
  "signIn.heading": "Consola Walliyo",
  "signIn.forgot": "¿Has olvidado la contraseña?",
  "signIn.environment": "Entorno",
  "signIn.build": "Compilación",
  "signIn.googleUnavailable":
    "El acceso con Google no está disponible. Use su correo y contraseña, o avise al propietario.",
  "error.setPassword.invalid_code":
    "Este email y este código no corresponden a ninguna invitación abierta. Compruebe ambos; si el código se ha perdido o ha caducado, pida que le inviten de nuevo.",
  "error.setPassword.too_many_attempts":
    "Demasiados códigos incorrectos: este código ya no es válido. Pida que le inviten de nuevo.",
  "error.setPassword.validation_error":
    "La contraseña no cumple las reglas, o el código no tiene el formato correcto. Corríjalo y vuelva a intentarlo.",

  // ── Dossiers (the support case queue) ──
  "cases.newCall": "+ Registrar una llamada",
  "cases.queue.aria": "Cola de expedientes",
  "cases.queue.views": "Vistas de la cola",
  "cases.view.all": "Todos",
  "cases.view.unassigned": "Sin asignar",
  "cases.view.mine": "Los míos",
  "cases.view.late": "Con retraso",
  "cases.filter.status": "Estado",
  "cases.filter.notClosed": "Todos menos cerrados",
  "cases.filter.channel": "Canal",
  "cases.filter.anyChannel": "Todos los canales",
  "cases.filter.customer": "Cliente {id}",
  "cases.filter.clear": "Quitar el filtro de cliente",
  "cases.col.case": "Expediente",
  "cases.col.deadline": "Plazo",
  "cases.queue.loading": "Cargando la cola…",
  "cases.queue.loadFailed": "No se pudo cargar la cola.",
  "cases.queue.empty": "Ningún expediente coincide con estos filtros.",
  "cases.loadMore": "Cargar más",
  "cases.loadingMore": "Cargando…",
  "cases.retry": "Reintentar",
  "cases.pick": "Elija un expediente de la cola para ver su cronología.",
  "cases.loading": "Cargando el expediente…",
  "cases.loadFailed": "No se pudo cargar este expediente.",
  "cases.notFound": "Ningún expediente tiene este identificador. Quizá se copió mal.",
  "cases.refreshFailed": "No se pudo actualizar el expediente: lo que ve puede estar desfasado.",
  "cases.deadline.late": "Con retraso · {time}",
  "cases.deadline.left": "quedan {time}",
  "cases.deadline.replied": "Respondido",
  "cases.deadline.closed": "Cerrado",
  "cases.channel.phone": "Llamada",
  "cases.channel.email": "Correo",
  "cases.channel.chat": "Chat en la app",
  "cases.status.open": "Abierto",
  "cases.status.waiting_on_customer": "Esperando al cliente",
  "cases.status.closed": "Cerrado",
  "access.mine.title": "Lo que has pedido",
  "access.mine.none": "No has pedido nada.",
  "access.mine.what": "Qué",
  "access.mine.state": "En qué punto está",
  "access.mine.asked": "Pedido",
  "access.mine.withWhom": "Con",
  "access.mine.waiting": "Esperando",
  "access.mine.granted": "Concedido",
  "access.mine.refused": "Rechazado",
  "access.mine.overdue": "Atrasado",
  "access.mine.nobody": "Nadie todavía",
  "access.mine.answer": "Respuesta",
  "access.mine.loading": "Cargando tus solicitudes…",
  "access.mine.loadFailed": "No se han podido leer tus solicitudes.",
  "access.ask.title": "Pedir más",
  "access.ask.explain":
    "Construir la cosa no es razón para tener sus datos. Pide lo mínimo que te permita terminar, por el tiempo que lo necesites. Va a quien pueda concederlo, se te concede solo a ti, y termina por sí solo.",
  "access.ask.what": "Qué necesitas",
  "access.ask.until": "Hasta",
  "access.ask.why": "Por qué lo necesitas",
  "access.ask.whyHint": "Qué estás haciendo, y por qué esto es lo mínimo que te lo permite",
  "access.ask.send": "Enviar la solicitud",
  "access.ask.pickOne": "Elige qué necesitas",
  "access.ask.pastDate": "Indica una fecha futura, o déjalo abierto.",
  "access.ask.sent": "Enviado. Es el caso {reference}.",
  "cases.kind.access": "Solicitud de acceso",
  "cases.kind.customer": "Cliente",
  "cases.kind": "Tipo",
  "cases.access.wants": "Pide",
  "cases.access.until": "Hasta",
  "cases.access.grant": "Conceder",
  "cases.access.refuse": "Rechazar",
  "cases.access.own": "No puedes responder a tu propia solicitud.",
  "cases.access.explain":
    "Concedido solo a esta persona, nunca a su equipo. Termina en la fecha pedida, y cualquiera con autoridad puede cortarlo antes.",
  "cases.priority": "Prioridad: {priority}",
  "cases.priority.low": "Baja",
  "cases.priority.normal": "Normal",
  "cases.priority.high": "Alta",
  "cases.priority.urgent": "Urgente",
  "cases.unknownCaller": "Llamante desconocido",
  "cases.unknownCallerPhone": "Llamante desconocido · {phone}",
  "cases.customerWithPhone": "{name} · {phone}",
  "cases.ownedBy": "asignado a {name}",
  "cases.unassigned": "sin asignar",
  "cases.closedBecause": "Cerrado · motivo: {reason}",
  "cases.timeline.aria": "Cronología del expediente",
  "cases.timeline.empty": "Todavía no hay nada registrado en este expediente.",
  "cases.internal": "Interno",
  "cases.entry.call": "Llamada",
  "cases.entry.callWithDuration": "Llamada ({duration})",
  "cases.entry.note": "Nota interna",
  "cases.entry.chatIn": "Mensaje del cliente en la app",
  "cases.entry.chatOut": "Respuesta en la app",
  "cases.entry.smsIn": "SMS del cliente",
  "cases.entry.smsOut": "Respuesta por SMS",
  "cases.entry.smsOutTo": "SMS al {phone}",
  "cases.entry.emailIn": "Correo del cliente",
  "cases.entry.emailOut": "Respuesta por correo",
  "cases.sms.pending": "Enviándose",
  "cases.sms.sent": "Enviado",
  "cases.sms.failed": "No enviado",
  "cases.action.claim": "Tomar",
  "cases.action.reassign": "Reasignar",
  "cases.action.link": "Vincular a un cliente",
  "cases.action.relink": "Cambiar de cliente",
  "cases.action.close": "Cerrar",
  "cases.needs": "Requiere {feature}",
  "cases.sending": "Enviando…",
  "cases.cancel": "Cancelar",
  "cases.reload": "Recargar el expediente",
  "cases.reason": "Motivo",
  "cases.reasonHint": "Se guarda en el registro de auditoría con su nombre.",
  "cases.stale":
    "Este expediente cambió mientras lo consultaba. Recárguelo, compruebe qué cambió y vuelva a intentarlo: su texto se conserva.",
  "cases.claim.done": "{reference} es suyo.",
  "cases.claim.taken": "Demasiado tarde: {name} acaba de tomar este expediente. Elija otro.",
  "cases.composer.aria": "Escribir en el expediente",
  "cases.composer.reply": "Responder",
  "cases.composer.note": "Nota interna",
  "cases.composer.call": "Registrar una llamada",
  "cases.reply.noChannel":
    "No hay ninguna cuenta de cliente vinculada: vincula al cliente para responder en la app, o devuélvele la llamada.",
  "cases.reply.notified": "El cliente recibe una notificación en la app. Nunca un SMS.",
  "cases.reply.label": "Respuesta a {name}",
  "cases.reply.sendChat": "Enviar en la app",
  "cases.reply.doneChat": "Respuesta enviada en la app.",
  "cases.reply.closed": "Este expediente está cerrado: ya no admite respuestas.",
  "cases.reply.unassigned": "Tome este expediente para responder al cliente.",
  "cases.reply.notOwner":
    "Solo el responsable del expediente responde al cliente. Pida una reasignación si hace falta.",
  "cases.refused.claimed": "Demasiado tarde: otra persona acaba de tomar este caso. Elige otro.",
  "cases.refused.closed": "Este caso se cerró mientras tanto. Recárgalo.",
  "cases.refused.notOwner": "Este caso ahora pertenece a otra persona. Recárgalo, o pide que te lo reasignen.",
  "cases.refused.noCustomer":
    "No hay ninguna cuenta de cliente vinculada: vincula al cliente para responder en la app, o devuélvele la llamada.",
  "cases.note.hint": "Solo lo ve el equipo, nunca el cliente.",
  "cases.note.label": "Nota interna",
  "cases.note.submit": "Añadir la nota",
  "cases.note.done": "Nota interna añadida.",
  "cases.call.label": "Lo que se dijo (resumen interno)",
  "cases.call.minutes": "Duración (minutos)",
  "cases.call.submit": "Registrar la llamada",
  "cases.call.done": "Llamada registrada.",
  "cases.reassign.title": "Reasignar {reference}",
  "cases.reassign.from": "Asignado actualmente a {name}.",
  "cases.reassign.unassigned": "Este expediente aún no está asignado a nadie.",
  "cases.reassign.to": "Nuevo responsable",
  "cases.reassign.choose": "Elegir un miembro del equipo",
  "cases.reassign.loading": "Cargando el equipo…",
  "cases.reassign.toId": "Identificador del miembro del equipo",
  "cases.reassign.idHint": "Sin acceso a la lista del equipo, pegue el identificador del miembro.",
  "cases.reassign.submit": "Reasignar",
  "cases.reassign.done": "Expediente reasignado a {name}.",
  "cases.close.title": "Cerrar {reference}",
  "cases.close.body": "El cliente ya no podrá responder; si vuelve, se abrirá un expediente nuevo.",
  "cases.close.submit": "Cerrar el expediente",
  "cases.close.done": "{reference} está cerrado.",
  "cases.link.title": "Vincular {reference} a un cliente",
  "cases.link.body": "Busque la cuenta del cliente que contactó con soporte.",
  "cases.link.caller": "El llamante dio el {phone}. Busque su cuenta.",
  "cases.link.current": "Vinculado actualmente a {name}.",
  "cases.link.submit": "Vincular",
  "cases.link.done": "Expediente vinculado a {name}.",
  "cases.customer.search": "Buscar al cliente",
  "cases.customer.placeholder": "Nombre, teléfono, correo o identificador",
  "cases.customer.searchButton": "Buscar",
  "cases.customer.searching": "Buscando…",
  "cases.customer.none": "No se encontró ninguna cuenta.",
  "cases.customer.results": "Cuentas encontradas",
  "cases.customer.id": "Identificador de la cuenta del cliente",
  "cases.customer.idHint": "La búsqueda requiere {feature}: pegue el identificador de la cuenta.",
  "cases.newCall.title": "Registrar una llamada",
  "cases.newCall.who": "¿Quién llamó?",
  "cases.newCall.known": "Un cliente",
  "cases.newCall.unknown": "Un llamante desconocido",
  "cases.newCall.phone": "Número del llamante",
  "cases.newCall.phoneHint": "En formato internacional, por ejemplo +224 621 23 45 67.",
  "cases.newCall.subject": "Asunto",
  "cases.newCall.internal": "Interno: el cliente nunca ve este resumen.",
  "cases.newCall.priority": "Prioridad",
  "cases.newCall.submit": "Abrir el expediente",
  "cases.problem.reason": "Diga por qué, en pocas palabras.",
  "cases.problem.body": "Escriba algo.",
  "cases.problem.tooLong": "Demasiado largo: {max} caracteres como máximo.",
  "cases.problem.minutes": "La duración es un número entero de minutos, de 0 a {max}.",
  "cases.problem.phone": "El número debe estar en formato internacional, por ejemplo +224621234567.",
  "cases.problem.pickCustomer": "Elija al cliente.",
  "cases.problem.customerId": "Este identificador de cuenta no tiene el formato correcto.",
  "cases.problem.pickStaff": "Elija a quién se asigna el expediente.",
  "cases.problem.staffId": "Este identificador de miembro no tiene el formato correcto.",
  "cases.problem.subject": "Dé al expediente un asunto breve.",
  // ── Verificaciones ──
  "verifications.summary.open": "{count} en espera · la más antigua desde hace {age}",
  "verifications.summary.none": "Ninguna verificación en espera",
  "verifications.summary.shown": "{count} verificaciones mostradas",
  "verifications.filter.status": "Estado",
  "verifications.filter.status.open": "Todas en espera",
  "verifications.filter.status.submitted": "Por revisar",
  "verifications.filter.status.flagged": "Documento prohibido",
  "verifications.filter.status.approved": "Aprobadas",
  "verifications.filter.status.rejected": "Rechazadas",
  "verifications.filter.document": "Tipo de documento",
  "verifications.filter.document.all": "Todos",
  "verifications.filter.document.national_id": "Documento de identidad",
  "verifications.filter.document.passport": "Pasaporte",
  "verifications.queue.aria": "Cola de verificaciones, la más antigua primero",
  "verifications.queue.loading": "Cargando la cola…",
  "verifications.queue.loadFailed": "No se pudo cargar la cola.",
  "verifications.queue.emptyOpen": "Ninguna verificación en espera: la cola está vacía.",
  "verifications.queue.empty": "Ninguna verificación coincide con estos filtros.",
  "verifications.queue.waited": "Tiempo de espera desde el envío",
  "verifications.queue.end": "Fin de la cola.",
  "verifications.flag.banned": "Documento prohibido",
  "verifications.status.submitted": "Por revisar",
  "verifications.status.flagged": "Documento prohibido",
  "verifications.status.approved": "Aprobada",
  "verifications.status.rejected": "Rechazada",
  "verifications.document.national_id": "Documento de identidad",
  "verifications.document.passport": "Pasaporte",
  "verifications.side.front": "Anverso",
  "verifications.side.back": "Reverso",
  "verifications.side.selfie": "Foto selfi",
  "verifications.detail.pick": "Elija una verificación de la cola para revisarla.",
  "verifications.detail.loading": "Cargando la verificación…",
  "verifications.detail.loadFailed": "No se pudo cargar esta verificación.",
  "verifications.detail.notFound": "Ninguna verificación tiene este identificador. Recargue la cola.",
  "verifications.detail.noPhone": "sin teléfono",
  "verifications.detail.submitted": "enviada el {date}",
  "verifications.detail.versionTitle": "Versión revisada: una decisión solo se aplica a esta versión",
  "verifications.banned.title": "Documento prohibido",
  "verifications.banned.body":
    "Este número corresponde a un documento prohibido el {date} · motivo: {reason}. Nunca puede aprobarse: rechace la verificación.",
  "verifications.banned.bodyNoReason":
    "Este número corresponde a un documento prohibido. Nunca puede aprobarse: rechace la verificación.",
  "verifications.banned.notTold": "Al pro no se le informa de que su documento está prohibido.",
  "verifications.images.aria": "Documentos de identidad",
  "verifications.images.show": "Mostrar los documentos",
  "verifications.images.showAgain": "Mostrar de nuevo",
  "verifications.images.loading": "Cargando…",
  "verifications.images.notice":
    "Cada visualización queda registrada en el registro de auditoría, a su nombre, y aparece en el historial del pro. Los enlaces caducan a los 2 minutos.",
  "verifications.images.recorded":
    "Visualización registrada en el registro de auditoría · enlaces válidos hasta las {time}",
  "verifications.images.hide": "Ocultar",
  "verifications.images.expired":
    "Los enlaces caducaron y las imágenes se retiraron de la pantalla. Mostrarlas de nuevo también queda registrado.",
  "verifications.images.locked": "Las imágenes de identidad requieren",
  "verifications.images.deleted": "Las imágenes se eliminaron el {date}, como exige la regla de conservación.",
  "verifications.col.field": "Campo",
  "verifications.col.read": "Leído en el documento",
  "verifications.col.typed": "Introducido por el pro",
  "verifications.col.match": "Coincidencia",
  "verifications.field.name": "Nombre",
  "verifications.field.dateOfBirth": "Nacido el",
  "verifications.field.expiresOn": "Caduca el",
  "verifications.field.documentNumber": "N.º de documento",
  "verifications.notAsked": "no solicitado",
  "verifications.match.check": "Comprobar en la imagen",
  "verifications.match.same": "Coincide",
  "verifications.match.different": "No coincide",
  "verifications.match.readOnly": "Solo leído",
  "verifications.match.expired": "Caducado",
  "verifications.match.valid": "Vigente",
  "verifications.match.banned": "Prohibido",
  "verifications.match.notBanned": "No prohibido",
  "verifications.last4Hint":
    "Solo se conservan los 4 últimos caracteres del número ({last4}): compárelos con la imagen.",
  "verifications.standing.title": "Cuenta del pro hoy",
  "verifications.standing.verificationStatus": "Estado de verificación",
  "verifications.standing.approvalStatus": "Aprobación del perfil",
  "verifications.standing.isVerified": "Insignia «verificado»",
  "verifications.standing.status.unverified": "No verificado",
  "verifications.standing.status.pending": "En revisión",
  "verifications.standing.status.verified": "Verificado",
  "verifications.standing.status.rejected": "Rechazado",
  "verifications.standing.approval.pending": "Por aprobar",
  "verifications.standing.approval.approved": "Aprobado",
  "verifications.standing.approval.rejected": "No aprobado",
  "verifications.yes": "Sí",
  "verifications.no": "No",
  "verifications.previous.title": "Envíos anteriores",
  "verifications.previous.none": "Primer envío de este pro.",
  "verifications.approve.button": "Aprobar al pro",
  "verifications.reject.button": "Rechazar",
  "verifications.decided": "Esta verificación ya está decidida.",
  "verifications.approve.banned":
    "No se puede aprobar: el número corresponde a un documento prohibido. Rechace la verificación.",
  "verifications.approve.locked":
    "Aprobar también requiere APPROVE_PROS_STAFF_FEATURE_ENABLEMENT, que sus roles no incluyen.",
  "verifications.decided.approved": "Aprobada por {name} el {date}.",
  "verifications.decided.rejected": "Rechazada por {name} el {date}.",
  "verifications.decided.reasonCode": "Motivo: {reason}",
  "verifications.decided.reason": "Nota del equipo: «{reason}»",
  "verifications.outcome.approved":
    "{name} está aprobado: su perfil está verificado y se le puede encontrar y contratar.",
  "verifications.outcome.rejected": "Verificación rechazada. El profesional recibe un aviso en la app, en su idioma.",
  "verifications.approve.title": "Aprobar a {name}",
  "verifications.approve.body":
    "Se acepta el documento y el pro queda verificado: se le puede encontrar y contratar. La decisión queda en el registro de auditoría con su motivo.",
  "verifications.approve.reason": "Motivo (queda en el registro de auditoría)",
  "verifications.approve.hint": "P. ej. «Documento legible, selfi coincide».",
  "verifications.approve.submit": "Aprobar al pro",
  "verifications.approve.tooShort": "Indique un motivo de al menos {min} caracteres.",
  "verifications.reject.title": "Rechazar la verificación de {name}",
  "verifications.reject.body": "El pro podrá enviar un nuevo documento desde la aplicación.",
  "verifications.reject.preset": "Motivo del rechazo",
  "verifications.reject.presetNone": "Elegir un motivo…",
  "verifications.reject.code.photo_unreadable": "Foto ilegible",
  "verifications.reject.code.selfie_mismatch": "El selfie no coincide",
  "verifications.reject.code.document_expired": "Documento caducado",
  "verifications.reject.code.document_not_accepted": "Documento no aceptado",
  "verifications.reject.code.other": "Otro",
  "verifications.reject.reason": "Nota para el equipo (opcional)",
  "verifications.reject.noteHint":
    "Nunca se muestra al profesional: recibe en la app el mensaje del motivo elegido, en su idioma.",
  "verifications.reject.submit": "Rechazar la verificación",
  "verifications.reject.pickCode": "Elige el motivo del rechazo.",
  "verifications.reason.tooLong": "El motivo puede tener como máximo {max} caracteres.",
  "verifications.reason.count": "{count} de {max} caracteres",
  "verifications.dialog.version": "Decisión sobre la versión {version}",
  "verifications.dialog.cancel": "Cancelar",
  "verifications.dialog.sending": "Enviando…",
  "verifications.error.stale":
    "Esta verificación cambió mientras la miraba (quizá otra persona ya la decidió). Recargue para ver la versión actual y decida de nuevo.",
  "verifications.error.approveRefused":
    "Aprobación rechazada: sus roles no incluyen REVIEW_VERIFICATIONS y APPROVE_PROS, o el documento acaba de ser prohibido. Recargue.",
  "verifications.error.rejectRefused":
    "No se puede rechazar: sus roles ya no incluyen REVIEW_VERIFICATIONS. Pida acceso a un responsable.",
  "verifications.error.imagesRefused":
    "Imágenes denegadas: sus roles ya no incluyen VIEW_ID_IMAGES. Este intento queda en el registro de auditoría.",
  "verifications.error.approveInvalid":
    "Aprobación rechazada: la verificación ya está decidida, el documento está prohibido o el motivo no es válido. Recargue y compruebe.",
  "verifications.error.rejectInvalid":
    "Rechazo denegado: la verificación ya está decidida, o la nota supera los {max} caracteres.",
  "verifications.error.imagesDeleted":
    "Las imágenes ya no están disponibles: se eliminaron según la regla de conservación, o no se encuentra la verificación.",
  "verifications.error.outcomeUnknown":
    "Sin respuesta del servidor: puede que la decisión se haya guardado. Vuelva a intentarlo: el mismo clic no puede aplicarse dos veces.",
};
